import { useState, useEffect } from 'react'
import { jsPDF } from 'jspdf'
import { X, Download, FileText, Palette, Grid3X3, Clock, Scissors, Ruler } from 'lucide-react'

// Symbol set for grid-based patterns - easy to distinguish
const SYMBOLS = [
  '■', '●', '▲', '◆', '★', '♦', '♠', '♣', '♥', '○',
  '□', '△', '◇', '☆', '▼', '◀', '▶', '⬟', '⬡', '⬢',
  'X', '+', '/', '\\', '=', '#', '@', '&', '%', '$',
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J',
  'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T'
]

// =============================================================================
// CRAFT-SPECIFIC CONFIGURATIONS
// Each craft has: label, isGridBased, unitLabel, colorLabel, sizeIcon,
//   materials(), instructions, pdfIncludes[], sizeCalc()
// =============================================================================
const CRAFT_CONFIGS = {

  // =========================================================================
  //  GRID-BASED CRAFTS (symbol chart + stitch grid in PDF)
  // =========================================================================

  'cross-stitch': {
    label: 'Cross Stitch',
    isGridBased: true,
    unitLabel: 'stitches',
    colorLabel: 'threads',
    colorCodeLabel: 'DMC Code',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      `14-count white or cream Aida cloth (${Math.ceil(w / 14) + 4}" x ${Math.ceil(h / 14) + 4}" minimum)`,
      'Size 24 tapestry needle',
      '6-inch embroidery hoop',
      'Small scissors for trimming',
      `DMC embroidery floss in ${colors} colors (1 skein each)`,
      'Pattern chart and color key'
    ],
    instructions: 'Start stitching from the center point of the fabric. Use 2 strands of floss for cross-stitches and 1 strand for any backstitch details. Work systematically row by row or by color to minimize thread changes. Each symbol on the chart represents one cross-stitch.',
    pdfIncludes: [
      '✓ Complete stitch grid with symbols',
      '✓ Color key with DMC thread codes',
      '✓ Materials shopping list',
      '✓ Step-by-step instructions',
      '✓ Stitch count per color',
      '✓ Row/column numbers for easy tracking'
    ],
    sizeCalc: (w, h) => `${Math.round(w / 14)}" x ${Math.round(h / 14)}" on 14-count Aida`,
  },

  'knitting': {
    label: 'Knitting',
    isGridBased: true,
    unitLabel: 'stitches',
    colorLabel: 'yarn colors',
    colorCodeLabel: 'Yarn Color',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      `Worsted weight yarn in ${colors} colors`,
      'US 7 (4.5mm) knitting needles or size to obtain gauge',
      'Tapestry needle for weaving in ends',
      'Stitch markers',
      'Row counter',
      'Scissors'
    ],
    instructions: 'Read chart from bottom to top. Right-side rows read right to left, wrong-side rows read left to right. Use the intarsia or stranded colorwork method depending on color placement. Maintain consistent tension across color changes.',
    pdfIncludes: [
      '✓ Complete colorwork chart with symbols',
      '✓ Color key with yarn requirements',
      '✓ Materials shopping list',
      '✓ Step-by-step instructions',
      '✓ Stitch count per color',
      '✓ Row/column numbers for tracking'
    ],
    sizeCalc: (w, h) => `Approx ${Math.round(w / 5)}" x ${Math.round(h / 7)}" at standard worsted gauge`,
  },

  'crochet': {
    label: 'Crochet',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'yarn colors',
    colorCodeLabel: 'Yarn Color',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Worsted weight yarn in ${colors} colors`,
      '5.0mm (H/8) crochet hook (or as specified)',
      'Tapestry needle for weaving in ends',
      'Stitch markers',
      'Row counter',
      'Scissors',
      'Blocking mat and pins (for finishing)'
    ],
    instructions: 'Follow the design reference image for shaping and color placement. Use the specified yarn weights and hook sizes. Work in rounds or rows as appropriate for the design. Change colors by pulling new color through last two loops of the stitch before the color change. Block finished piece for professional results.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color palette with yarn recommendations',
      '✓ Complete materials and tools list',
      '✓ Step-by-step construction instructions',
      '✓ Stitch type guide for each section',
      '✓ Finishing and blocking tips'
    ],
    sizeCalc: (w, h) => `Design template — adjust to desired finished size`,
  },

  'crochet-chart': {
    label: 'Crochet Chart',
    isGridBased: true,
    unitLabel: 'stitches',
    colorLabel: 'yarn colors',
    colorCodeLabel: 'Yarn Color',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      `Worsted weight yarn in ${colors} colors`,
      '5.0mm (H/8) crochet hook or size to obtain gauge',
      'Tapestry needle for weaving in ends',
      'Stitch markers',
      'Row counter',
      'Scissors'
    ],
    instructions: 'Work in single crochet using the tapestry crochet or C2C (corner-to-corner) method. Each square on the chart represents one single crochet stitch. Change colors by pulling new color through last two loops of the stitch before the color change.',
    pdfIncludes: [
      '✓ Complete stitch chart with symbols',
      '✓ Color key with yarn requirements',
      '✓ Materials shopping list',
      '✓ Step-by-step instructions',
      '✓ Stitch count per color',
      '✓ Row/column numbers for tracking'
    ],
    sizeCalc: (w, h) => `Approx ${Math.round(w / 4)}" x ${Math.round(h / 4)}" at standard SC gauge`,
  },

  'perler-beads': {
    label: 'Perler Beads',
    isGridBased: true,
    unitLabel: 'beads',
    colorLabel: 'bead colors',
    colorCodeLabel: 'Perler Color',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      `Perler beads in ${colors} colors`,
      `${Math.ceil(w / 29)} x ${Math.ceil(h / 29)} pegboard(s) — standard 29x29`,
      'Ironing paper / parchment paper',
      'Household iron',
      'Tweezers (optional, for precise placement)'
    ],
    instructions: 'Place beads on pegboard following the chart. Each cell = one bead. When complete, cover with ironing paper and press with medium-hot iron for 10-20 seconds until beads fuse. Let cool flat under a heavy book.',
    pdfIncludes: [
      '✓ Complete bead layout chart',
      '✓ Color key with Perler bead color names',
      '✓ Materials shopping list',
      '✓ Step-by-step instructions',
      '✓ Bead count per color',
      '✓ Row/column numbers for tracking'
    ],
    sizeCalc: (w, h) => `${Math.round(w * 0.2)}" x ${Math.round(h * 0.2)}" (5mm beads)`,
  },

  'diamond-painting': {
    label: 'Diamond Painting',
    isGridBased: true,
    unitLabel: 'diamonds',
    colorLabel: 'diamond colors',
    colorCodeLabel: 'DMC Equivalent',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      `Diamond painting canvas (${Math.round(w / 2.5)}" x ${Math.round(h / 2.5)}" recommended)`,
      `Diamond drills in ${colors} colors (round or square)`,
      'Diamond applicator pen',
      'Wax pad',
      'Sorting trays',
      'Roller or sealant for finishing'
    ],
    instructions: 'Peel back a small section of the protective film. Use the applicator pen dipped in wax to pick up diamonds one at a time and place on the corresponding symbol. Work in small sections to prevent the adhesive from drying out.',
    pdfIncludes: [
      '✓ Complete diamond placement chart',
      '✓ Color key with DMC-equivalent codes',
      '✓ Materials shopping list',
      '✓ Step-by-step instructions',
      '✓ Diamond count per color',
      '✓ Row/column numbers for tracking'
    ],
    sizeCalc: (w, h) => `${Math.round(w / 2.5)}" x ${Math.round(h / 2.5)}"`,
  },

  'plastic-canvas': {
    label: 'Plastic Canvas',
    isGridBased: true,
    unitLabel: 'stitches',
    colorLabel: 'yarn colors',
    colorCodeLabel: 'Yarn Color',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      '7-count plastic canvas sheet(s)',
      `Worsted weight yarn in ${colors} colors`,
      'Plastic canvas needle (size 16)',
      'Scissors',
      'Pattern chart'
    ],
    instructions: 'Cut plastic canvas to size. Work continental or tent stitches following the chart. Each square = one intersection on the canvas. Overcast edges when complete.',
    pdfIncludes: [
      '✓ Complete stitch chart with symbols',
      '✓ Color key with yarn requirements',
      '✓ Materials shopping list',
      '✓ Step-by-step instructions',
      '✓ Stitch count per color',
      '✓ Row/column numbers for tracking'
    ],
    sizeCalc: (w, h) => `${Math.round(w / 7)}" x ${Math.round(h / 7)}" on 7-count canvas`,
  },

  'latch-hook': {
    label: 'Latch Hook',
    isGridBased: true,
    unitLabel: 'knots',
    colorLabel: 'yarn colors',
    colorCodeLabel: 'Yarn Color',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      `Latch hook canvas (${Math.round(w / 3.75)}" x ${Math.round(h / 3.75)}" at 3.75 mesh)`,
      `Pre-cut yarn in ${colors} colors`,
      'Latch hook tool',
      'Rug binding tape',
      'Non-slip rug pad (optional)'
    ],
    instructions: 'Work from the bottom of the canvas upward, row by row. Fold a yarn piece over the hook, latch through the canvas mesh, and pull through to create each knot. Follow the chart — each square = one knot.',
    pdfIncludes: [
      '✓ Complete chart with symbols',
      '✓ Color key with yarn requirements',
      '✓ Materials shopping list',
      '✓ Step-by-step instructions',
      '✓ Knot count per color',
      '✓ Row/column numbers for tracking'
    ],
    sizeCalc: (w, h) => `${Math.round(w / 3.75)}" x ${Math.round(h / 3.75)}" at 3.75 mesh`,
  },

  'mosaic': {
    label: 'Mosaic',
    isGridBased: true,
    unitLabel: 'tiles',
    colorLabel: 'tile colors',
    colorCodeLabel: 'Tile Color',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      `Mosaic tiles in ${colors} colors`,
      'Tile adhesive / mortar',
      'Grout and grout float',
      'Tile nippers',
      'Substrate board or surface',
      'Sealant'
    ],
    instructions: 'Transfer the pattern grid onto your substrate. Apply adhesive in small sections. Place tiles following the color chart. Allow to cure 24 hours, then grout and seal.',
    pdfIncludes: [
      '✓ Complete tile layout chart',
      '✓ Color key with tile specifications',
      '✓ Materials shopping list',
      '✓ Step-by-step instructions',
      '✓ Tile count per color',
      '✓ Row/column numbers for tracking'
    ],
    sizeCalc: (w, h) => `Varies with tile size`,
  },

  'pixel-art': {
    label: 'Pixel Art',
    isGridBased: true,
    unitLabel: 'pixels',
    colorLabel: 'colors',
    colorCodeLabel: 'Hex Code',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      `Digital canvas: ${w} x ${h} pixels`,
      `Color palette: ${colors} colors`,
      'Pixel art software (Aseprite, Piskel, or similar)',
    ],
    instructions: 'Recreate pixel-by-pixel following the chart. Each cell = one pixel. Use the color key to match exact colors.',
    pdfIncludes: [
      '✓ Complete pixel grid with colors',
      '✓ Color key with hex codes',
      '✓ Pixel count per color',
      '✓ Row/column numbers for tracking'
    ],
    sizeCalc: (w, h) => `${w} x ${h} pixels`,
  },

  'beading': {
    label: 'Beading',
    isGridBased: true,
    unitLabel: 'beads',
    colorLabel: 'bead colors',
    colorCodeLabel: 'Bead Color',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      `Seed beads in ${colors} colors (size 11/0 recommended)`,
      'Beading needle (size 12)',
      'Beading thread (FireLine or Nymo)',
      'Bead loom or beading mat',
      'Scissors'
    ],
    instructions: 'Set up loom with warp threads. Follow chart row by row, threading beads in order. Each cell = one bead. For peyote stitch, work in offset rows following the adapted chart.',
    pdfIncludes: [
      '✓ Complete bead layout chart',
      '✓ Color key with bead specifications',
      '✓ Materials shopping list',
      '✓ Step-by-step instructions',
      '✓ Bead count per color',
      '✓ Row/column numbers for tracking'
    ],
    sizeCalc: (w, h) => `Approx ${Math.round(w * 1.4 / 25.4)}" x ${Math.round(h * 1.8 / 25.4)}" with 11/0 beads`,
  },

  'needlepoint': {
    label: 'Needlepoint',
    isGridBased: true,
    unitLabel: 'stitches',
    colorLabel: 'thread colors',
    colorCodeLabel: 'Thread Code',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      `Needlepoint canvas (${Math.ceil(w / 14) + 4}" x ${Math.ceil(h / 14) + 4}", 14 mesh)`,
      `Tapestry wool or Persian yarn in ${colors} colors`,
      'Size 22 tapestry needle',
      'Scroll frame or stretcher bars',
      'Scissors'
    ],
    instructions: 'Work tent stitches (basketweave preferred for minimal canvas distortion) following the chart. Each square = one stitch. Start from the center and work outward. Use a frame to keep canvas taut.',
    pdfIncludes: [
      '✓ Complete stitch chart with symbols',
      '✓ Color key with thread requirements',
      '✓ Materials shopping list',
      '✓ Step-by-step instructions',
      '✓ Stitch count per color',
      '✓ Row/column numbers for tracking'
    ],
    sizeCalc: (w, h) => `${Math.round(w / 14)}" x ${Math.round(h / 14)}" on 14-mesh canvas`,
  },

  'punch-needle': {
    label: 'Punch Needle',
    isGridBased: true,
    unitLabel: 'loops',
    colorLabel: 'yarn/floss colors',
    colorCodeLabel: 'Color',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      'Monks cloth or weavers cloth',
      `Yarn or embroidery floss in ${colors} colors`,
      'Punch needle tool (adjustable depth preferred)',
      'Embroidery hoop or frame (gripper preferred)',
      'Fabric marker or transfer pen',
      'Scissors'
    ],
    instructions: 'Transfer design to fabric. Stretch tightly in hoop/frame. Thread punch needle and work from the BACK of fabric. Push needle fully through, glide along surface, and repeat. Each cell represents one punch loop. Keep loops consistent in height.',
    pdfIncludes: [
      '✓ Complete design chart with color zones',
      '✓ Color key with yarn/floss requirements',
      '✓ Materials shopping list',
      '✓ Step-by-step instructions',
      '✓ Loop count per color',
      '✓ Row/column numbers for tracking'
    ],
    sizeCalc: (w, h) => `Approx ${Math.round(w / 6)}" x ${Math.round(h / 6)}" depending on loop density`,
  },

  // =========================================================================
  //  DESIGN-BASED CRAFTS (design image + instructions in PDF, NO grid)
  // =========================================================================

  'stained-glass': {
    label: 'Stained Glass',
    isGridBased: false,
    unitLabel: 'pieces',
    colorLabel: 'glass colors',
    colorCodeLabel: 'Glass Type',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `${colors} colors of art glass (Spectrum, Kokomo, or Wissmach)`,
      'Lead came or copper foil tape (for Tiffany method)',
      'Glass cutter and running pliers',
      'Glass grinder',
      'Soldering iron and 60/40 solder',
      'Flux (oleic acid or zinc chloride based)',
      'Pattern shears (3-blade for lead came or single for foil)',
      'Light table (optional but helpful)',
      'Safety glasses and work gloves',
      'Patina solution (black or copper, optional)',
      'Came bending tool (for lead came method)'
    ],
    instructions: 'Cut each numbered pattern piece out with pattern shears. Trace onto glass and score with glass cutter. Break along score lines and grind edges smooth. For copper foil method: wrap each piece in foil, flux, and solder. For lead came method: fit pieces into H-channel came and solder intersections. Apply patina and polish when complete.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color palette with glass type suggestions',
      '✓ Complete materials list',
      '✓ Step-by-step construction instructions',
      '✓ Cutting, leading, and soldering guide',
      '✓ Finishing and weatherproofing tips'
    ],
    sizeCalc: (w, h) => `Design template — scale to desired finished size`,
  },

  'cloisonne': {
    label: 'Cloisonné',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'enamel colors',
    colorCodeLabel: 'Enamel Color',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Metal base (copper, silver, or gold)',
      'Cloisonné wire (fine metal strips)',
      `Enamel powder/paste in ${colors} colors`,
      'Kiln or torch for firing',
      'Adhesive for wire placement',
      'Grinding and polishing supplies',
      'Safety equipment'
    ],
    instructions: 'Bend cloisonné wire to match the design outlines and affix to metal base. Fill each enclosed section with enamel paste in the designated color. Fire in kiln. Repeat fill and fire until cells are full. Grind level and polish to a smooth finish.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color palette with enamel suggestions',
      '✓ Complete materials list',
      '✓ Wire placement guide',
      '✓ Firing temperature and schedule',
      '✓ Finishing instructions'
    ],
    sizeCalc: () => `Design template — scale to desired finished size`,
  },

  'glass-etching': {
    label: 'Glass Etching',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'etch zones',
    colorCodeLabel: 'Zone',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Glass surface (vase, mirror, window, etc.)',
      'Etching cream (Armour Etch or similar)',
      'Vinyl stencil material or contact paper',
      'Craft knife or cutting machine (Cricut/Silhouette)',
      'Transfer tape',
      'Rubbing alcohol for cleaning',
      'Disposable brush or sponge',
      'Gloves and eye protection'
    ],
    instructions: 'Clean glass thoroughly with rubbing alcohol. Cut stencil from vinyl using design template. Apply stencil to glass, pressing firmly to prevent undercutting. Apply etching cream generously over exposed areas. Wait 5-10 minutes, then rinse thoroughly. Remove stencil carefully.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Stencil cutting guide',
      '✓ Complete materials list',
      '✓ Step-by-step etching instructions',
      '✓ Tips for clean edges',
      '✓ Finishing and care guide'
    ],
    sizeCalc: () => `Design template — scale to fit your glass surface`,
  },

  'embroidery': {
    label: 'Embroidery',
    isGridBased: false,
    unitLabel: 'design areas',
    colorLabel: 'floss colors',
    colorCodeLabel: 'DMC Code',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Embroidery fabric (linen, cotton, or pre-printed)',
      `DMC embroidery floss in ${colors} colors`,
      'Embroidery needles (sizes 3-9)',
      'Embroidery hoop (6-8 inch)',
      'Water-soluble fabric marker or transfer pen',
      'Small sharp scissors',
      'Transfer paper (optional)'
    ],
    instructions: 'Transfer design to fabric using your preferred method (tracing, iron-on, or water-soluble pen). Secure fabric in hoop. Use stitch types as indicated: satin stitch for filled areas, backstitch for outlines, French knots for dots, chain stitch for textured lines. Use 2-3 strands for most stitches, 1 strand for fine detail.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color palette with DMC floss codes',
      '✓ Suggested stitch types per area',
      '✓ Complete materials list',
      '✓ Transfer instructions',
      '✓ Stitch technique guide'
    ],
    sizeCalc: () => `Design template — transfer at desired size`,
  },

  'quilting': {
    label: 'Quilting',
    isGridBased: false,
    unitLabel: 'pieces',
    colorLabel: 'fabric colors',
    colorCodeLabel: 'Fabric',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Quilting cotton in ${colors} colors/prints`,
      'Rotary cutter and self-healing mat',
      'Quilting ruler (6" x 24")',
      'Sewing machine with quarter-inch foot',
      'Iron and pressing board',
      'Batting (cotton or poly-blend)',
      'Backing fabric',
      'Quilting pins or clips',
      'Thread (50wt cotton for piecing, 40wt for quilting)'
    ],
    instructions: 'Cut all pieces with 1/4" seam allowance added. Piece blocks following the design layout. Press seams to the dark side or open as indicated. Assemble rows and join. Layer backing, batting, and quilt top. Quilt as desired and bind edges.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color palette with fabric suggestions',
      '✓ Piece cutting guide',
      '✓ Complete materials list',
      '✓ Assembly instructions',
      '✓ Quilting and finishing tips'
    ],
    sizeCalc: () => `Design template — scale to desired quilt size`,
  },

  'sewing': {
    label: 'Sewing Pattern',
    isGridBased: false,
    unitLabel: 'pieces',
    colorLabel: 'fabric types',
    colorCodeLabel: 'Fabric',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Fabric in ${colors} types/colors as specified`,
      'Matching thread',
      'Sewing machine',
      'Scissors or rotary cutter',
      'Pins and/or clips',
      'Iron and pressing board',
      'Measuring tape',
      'Seam ripper',
      'Pattern paper for tracing'
    ],
    instructions: 'Cut pattern pieces on grain line as marked. Transfer all notches, darts, and markings. 5/8" seam allowance is included unless otherwise noted. Stitch with right sides together unless indicated. Press seams as you go for professional results.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Fabric and notions list',
      '✓ Piece identification guide',
      '✓ Complete materials list',
      '✓ Step-by-step construction order',
      '✓ Pressing and finishing instructions'
    ],
    sizeCalc: () => `Pattern template — adjust to measurements`,
  },

  'macrame': {
    label: 'Macramé',
    isGridBased: false,
    unitLabel: 'knots',
    colorLabel: 'cord colors',
    colorCodeLabel: 'Cord Type',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Macramé cord (3mm-5mm) in ${colors} colors`,
      'Wooden dowel, ring, or branch for mounting',
      'Scissors',
      'Measuring tape',
      'Comb or brush (for fringe)',
      'S-hooks or clothing rack for working',
      'Tape or pins for holding work'
    ],
    instructions: 'Cut cords to specified lengths (generally 4x the finished length). Mount onto dowel with lark\'s head knots. Follow the knotting pattern using square knots, spiral knots, and half hitches as indicated. Trim and comb out fringe at the end.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Cord color and length guide',
      '✓ Knot type reference',
      '✓ Complete materials list',
      '✓ Step-by-step knotting sequence',
      '✓ Finishing and hanging tips'
    ],
    sizeCalc: () => `Design template — cord lengths will determine final size`,
  },

  'paper-piecing': {
    label: 'Paper Piecing',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'fabric colors',
    colorCodeLabel: 'Fabric',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Quilting cotton in ${colors} colors`,
      'Foundation paper or freezer paper',
      'Sewing machine',
      'Small stitch length (1.5mm)',
      'Add-A-Quarter ruler',
      'Iron and pressing board',
      'Paper scissors and fabric scissors',
      'Glue pen (optional)'
    ],
    instructions: 'Print or trace pattern sections onto foundation paper. Sew fabric pieces in numerical order, right sides together against the paper. Trim seam allowances to 1/4". Press each seam before adding the next piece. Remove paper after all sections are joined.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color placement guide',
      '✓ Foundation paper templates',
      '✓ Complete materials list',
      '✓ Numbered piecing sequence',
      '✓ Assembly instructions'
    ],
    sizeCalc: () => `Design template — print at 100% for full size`,
  },

  'applique': {
    label: 'Appliqué',
    isGridBased: false,
    unitLabel: 'pieces',
    colorLabel: 'fabric colors',
    colorCodeLabel: 'Fabric',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Fabric in ${colors} colors`,
      'Fusible web (Heat n Bond or Wonder Under)',
      'Background fabric',
      'Matching thread or contrasting for decorative stitch',
      'Sharp scissors or rotary cutter',
      'Iron and pressing board',
      'Stabilizer (for machine appliqué)'
    ],
    instructions: 'Trace pattern pieces (reversed) onto fusible web paper side. Cut roughly around shapes. Fuse to wrong side of fabric and cut on traced lines. Remove paper backing, position on background, and press to fuse. Finish edges with blanket stitch, satin stitch, or raw edge as desired.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color placement guide',
      '✓ Piece templates (reversed for fusible)',
      '✓ Complete materials list',
      '✓ Layering order guide',
      '✓ Edge finishing options'
    ],
    sizeCalc: () => `Design template — scale to desired size`,
  },

  'string-art': {
    label: 'String Art',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'string colors',
    colorCodeLabel: 'String/Thread',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Wood board (stained or painted)',
      `Embroidery floss or craft string in ${colors} colors`,
      'Small nails or pins (3/4" wire nails)',
      'Hammer',
      'Pattern template (printed)',
      'Tape',
      'Scissors'
    ],
    instructions: 'Sand and finish wood board. Tape pattern template to board. Hammer nails at each marked point. Remove paper template carefully. Wind string between nails following the pattern guide, wrapping around each nail as indicated.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Nail placement template',
      '✓ String color and routing guide',
      '✓ Complete materials list',
      '✓ Step-by-step stringing sequence',
      '✓ Finishing tips'
    ],
    sizeCalc: () => `Design template — print to desired finished size`,
  },

  'weaving': {
    label: 'Weaving',
    isGridBased: false,
    unitLabel: 'rows',
    colorLabel: 'yarn colors',
    colorCodeLabel: 'Yarn',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Yarn in ${colors} colors (worsted or bulky weight)`,
      'Weaving loom (frame loom or rigid heddle)',
      'Weaving shuttle or tapestry needle',
      'Warp thread (cotton or linen)',
      'Comb or fork for beating',
      'Scissors',
      'Tapestry needle for finishing'
    ],
    instructions: 'Warp loom with desired number of ends. Follow color sequence from bottom to top, changing weft colors as shown in design. Beat each row firmly and evenly. For tapestry areas, use small butterfly shuttles for each color section. Finish by securing warp ends.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color sequence chart',
      '✓ Warp and weft specifications',
      '✓ Complete materials list',
      '✓ Weaving instructions',
      '✓ Finishing and fringe options'
    ],
    sizeCalc: () => `Design template — size depends on loom width`,
  },

  'needle-felting': {
    label: 'Needle Felting',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'wool colors',
    colorCodeLabel: 'Wool Roving',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Wool roving in ${colors} colors`,
      'Felting needles (36, 38, and 40 gauge)',
      'Foam felting pad or brush mat',
      'Finger guards',
      'Core wool (for 3D projects)',
      'Scissors'
    ],
    instructions: 'Start with base shape using core wool. Layer colored roving and needle repeatedly to compact fibers. Work from large areas to small details. Use coarser needles (36g) for shaping, finer needles (40g) for surface detail. Keep fingers clear of needle path.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color placement guide',
      '✓ Wool roving color list',
      '✓ Complete materials list',
      '✓ Shaping and layering instructions',
      '✓ Detail technique tips'
    ],
    sizeCalc: () => `Design template — 3D sculpture reference`,
  },

  'rug-tufting': {
    label: 'Rug Tufting',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'yarn colors',
    colorCodeLabel: 'Yarn',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Rug yarn in ${colors} colors`,
      'Tufting gun (cut pile or loop pile)',
      'Tufting cloth/monks cloth',
      'Tufting frame',
      'Projector for design transfer (recommended)',
      'Fabric marker',
      'Rug adhesive and backing fabric',
      'Scissors or clippers for trimming'
    ],
    instructions: 'Stretch tufting cloth on frame. Transfer design using projector or marker. Tuft each color section following the design, working from outlines inward. Trim pile to even height. Apply adhesive to back, attach backing fabric, and trim edges.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color zone mapping',
      '✓ Yarn type and quantity estimates',
      '✓ Complete materials list',
      '✓ Tufting sequence guide',
      '✓ Backing and finishing instructions'
    ],
    sizeCalc: () => `Design template — scale to desired rug size`,
  },

  // =========================================================================
  //  MACHINE / CUT CRAFTS (design-based)
  // =========================================================================

  'vinyl-decal': {
    label: 'Vinyl Decal',
    isGridBased: false,
    unitLabel: 'layers',
    colorLabel: 'vinyl colors',
    colorCodeLabel: 'Vinyl',
    sizeIcon: 'layers',
    materials: (w, h, colors) => [
      `Adhesive vinyl in ${colors} colors (Oracal 651 or similar)`,
      'Transfer tape',
      'Cutting machine (Cricut or Silhouette)',
      'Weeding tools',
      'Scraper/squeegee',
      'Rubbing alcohol for surface prep',
      'Ruler and scissors'
    ],
    instructions: 'Import design into cutting software. Mirror if needed for HTV. Cut each color layer separately. Weed excess vinyl carefully. Apply transfer tape and burnish. Position on clean surface and apply with scraper. Remove transfer tape slowly.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Layer separation guide',
      '✓ Vinyl color specifications',
      '✓ Complete materials list',
      '✓ Cut settings guide',
      '✓ Weeding and application tips'
    ],
    sizeCalc: () => `Design template — import to cutting software at desired size`,
  },

  'wood-burning': {
    label: 'Wood Burning',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'burn levels',
    colorCodeLabel: 'Shade',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Wood surface (basswood, birch plywood, or poplar)',
      'Wood burning tool with interchangeable tips',
      'Transfer paper or graphite paper',
      'Printed pattern template',
      'Sandpaper (220-400 grit)',
      'Pencil and eraser',
      'Wood finish (polyurethane, oil, or wax)'
    ],
    instructions: 'Sand wood surface smooth. Transfer design using graphite paper or trace lightly with pencil. Start with outlines using a fine tip at medium heat. Fill areas with appropriate shading techniques. Work from lightest to darkest tones. Seal finished piece with wood finish.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Shading/burn depth guide',
      '✓ Complete materials list',
      '✓ Tip selection guide',
      '✓ Step-by-step burning sequence',
      '✓ Finishing instructions'
    ],
    sizeCalc: () => `Design template — print to fit your wood surface`,
  },

  'laser-engraving': {
    label: 'Laser Engraving',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'power levels',
    colorCodeLabel: 'Setting',
    sizeIcon: 'layers',
    materials: (w, h, colors) => [
      'Material surface (wood, acrylic, leather, anodized aluminum, etc.)',
      'Laser engraver/cutter',
      'Computer with laser software',
      'Masking tape (for smoke protection)',
      'Cleaning supplies'
    ],
    instructions: 'Import design into laser software. Set material type and thickness. Adjust power and speed settings for desired depth. Run a test on scrap material. Focus laser properly. Engrave, then clean residue. Apply finish if needed.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Suggested power/speed settings',
      '✓ Material recommendations',
      '✓ Complete setup checklist',
      '✓ Step-by-step workflow',
      '✓ Finishing tips'
    ],
    sizeCalc: () => `Design template — scale in laser software`,
  },

  'scroll-saw': {
    label: 'Scroll Saw',
    isGridBased: false,
    unitLabel: 'pieces',
    colorLabel: 'wood types',
    colorCodeLabel: 'Wood',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Wood stock (Baltic birch plywood or hardwood)',
      'Scroll saw with fine blades (#2-#5)',
      'Drill with small bit (for interior cuts)',
      'Sandpaper (150-400 grit)',
      'Pattern template and spray adhesive',
      'Wood finish or paint',
      'Safety glasses'
    ],
    instructions: 'Attach pattern to wood with spray adhesive. Cut outline first, then interior cuts (drill entry holes for enclosed areas). Cut on the waste side of the line. Sand all edges smooth. Apply finish or paint as desired.',
    pdfIncludes: [
      '✓ Full-size cutting template',
      '✓ Blade selection guide',
      '✓ Wood type suggestions',
      '✓ Complete materials list',
      '✓ Cutting sequence guide',
      '✓ Finishing instructions'
    ],
    sizeCalc: () => `Cutting template — print at 100% for full size`,
  },

  'screen-printing': {
    label: 'Screen Printing',
    isGridBased: false,
    unitLabel: 'colors/screens',
    colorLabel: 'ink colors',
    colorCodeLabel: 'Ink',
    sizeIcon: 'layers',
    materials: (w, h, colors) => [
      `Screen printing screens (one per color — ${colors} total)`,
      `Screen printing ink in ${colors} colors`,
      'Photo emulsion and sensitizer',
      'Exposure unit or bright light',
      'Squeegee',
      'Transparency film for positives',
      'Masking tape and screen filler',
      'Press or hinge clamps',
      'Heat gun or conveyor dryer for curing'
    ],
    instructions: 'Print each color separation on transparency film. Coat screens with emulsion and expose each color layer. Wash out unexposed areas. Register screens using alignment marks. Print lightest color first, flash cure between colors. Heat set final print.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color separation guide',
      '✓ Ink specifications',
      '✓ Complete materials list',
      '✓ Registration and print order',
      '✓ Curing instructions'
    ],
    sizeCalc: () => `Design template — size for your substrate`,
  },

  // =========================================================================
  //  SURFACE DESIGN / ART CRAFTS (design-based)
  // =========================================================================

  'sublimation': {
    label: 'Sublimation',
    isGridBased: false,
    unitLabel: 'design area',
    colorLabel: 'colors',
    colorCodeLabel: 'CMYK Color',
    sizeIcon: 'layers',
    materials: (w, h, colors) => [
      'Sublimation printer with sublimation ink',
      'Sublimation paper',
      'Polyester-coated substrate (mug, shirt, tumbler, etc.)',
      'Heat press (flat or mug press)',
      'Heat-resistant tape',
      'Lint roller',
      'Butcher/protective paper'
    ],
    instructions: 'Print design MIRRORED onto sublimation paper. Prepare substrate (lint roll, preheat). Tape print to substrate face-down. Press at recommended time/temp (varies by substrate). Remove paper while hot. Allow to cool.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color profile information',
      '✓ Substrate recommendations',
      '✓ Complete materials list',
      '✓ Press time/temperature guide',
      '✓ Troubleshooting tips'
    ],
    sizeCalc: () => `Design template — size for your substrate`,
  },

  't-shirt-design': {
    label: 'T-Shirt Design',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'colors',
    colorCodeLabel: 'Color',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Blank t-shirt (cotton for screen print, polyester for sublimation)',
      'Transfer method supplies (HTV, sublimation, DTF, or screen print)',
      'Heat press or iron',
      'Cutting machine (for HTV)',
      'Protective paper/teflon sheet'
    ],
    instructions: 'Prepare design at correct size for placement. For HTV: mirror design, cut, weed, and heat press. For sublimation: print mirrored, press at 385°F for 60 seconds. For DTF: print, powder, cure, and press. Pre-press shirt to remove moisture.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color specifications',
      '✓ Placement guide',
      '✓ Complete materials list',
      '✓ Method-specific instructions',
      '✓ Care and washing tips'
    ],
    sizeCalc: () => `Design template — standard chest placement 10" x 12"`,
  },

  'coloring-page': {
    label: 'Coloring Page',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'suggested colors',
    colorCodeLabel: 'Color',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Printed coloring page (cardstock for markers)',
      'Colored pencils, markers, or crayons',
      'Fine-tip pen for details (optional)',
      'Blending stumps (for colored pencils)'
    ],
    instructions: 'Print the design on cardstock or heavy paper for best results. Color sections using your preferred medium. Start with lighter colors and build to darker shades. Use light pressure for layering with colored pencils.',
    pdfIncludes: [
      '✓ Full-size line art design',
      '✓ Suggested color palette',
      '✓ Printing recommendations',
      '✓ Coloring technique tips'
    ],
    sizeCalc: () => `Print at 100% on letter or A4 paper`,
  },

  'zentangle': {
    label: 'Zentangle',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'ink/pen types',
    colorCodeLabel: 'Pen',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Quality paper or tile (3.5" square traditional)',
      'Micron pens (01, 03, 05)',
      'Graphite pencil for shading',
      'Tortillion/blending stump',
      'White gel pen or charcoal pencil (optional)'
    ],
    instructions: 'Draw border and string (light pencil guidelines). Fill each section with a different tangle pattern. Work one section at a time. Add shading with pencil for dimension. There are no mistakes in Zentangle — embrace the meditative process.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Tangle pattern breakdown',
      '✓ Complete materials list',
      '✓ Step-by-step section guide',
      '✓ Shading techniques',
      '✓ Pattern inspiration reference'
    ],
    sizeCalc: () => `Design template — traditional 3.5" tile or scale up`,
  },

  'tattoo-flash': {
    label: 'Tattoo Flash',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'ink colors',
    colorCodeLabel: 'Ink',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Flash sheet paper or Bristol board',
      'Drawing pencils (HB, 2B)',
      'Micron pens or India ink',
      `Markers or watercolors in ${colors} colors`,
      'Lightbox (optional)',
      'Eraser and ruler'
    ],
    instructions: 'Sketch design lightly in pencil. Ink outlines with confident, clean lines. Add color fills with markers or watercolor. Traditional flash uses bold lines and solid color fills with minimal shading.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color palette guide',
      '✓ Line weight reference',
      '✓ Complete materials list',
      '✓ Style notes and history',
      '✓ Scaling guidelines'
    ],
    sizeCalc: () => `Design template — standard flash sheet size`,
  },

  'resin-art': {
    label: 'Resin Art',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'pigment colors',
    colorCodeLabel: 'Pigment',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Epoxy resin and hardener',
      `Resin pigments/dyes in ${colors} colors`,
      'Silicone mold or prepared surface',
      'Mixing cups and stir sticks',
      'Heat gun or torch (for bubbles)',
      'Gloves, respirator, and eye protection',
      'Level surface',
      'Inclusions (glitter, flowers, etc. — optional)'
    ],
    instructions: 'Prepare workspace (level, covered, ventilated). Mix resin per manufacturer ratios. Add pigments to separate cups. Pour base layer, then add colors. Manipulate with heat gun or stir stick. Pop bubbles with torch. Cure for 24-72 hours.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color palette with pigment types',
      '✓ Resin mixing ratios',
      '✓ Complete materials list',
      '✓ Pouring technique guide',
      '✓ Curing and finishing tips'
    ],
    sizeCalc: () => `Design reference — adapt to your mold/surface size`,
  },

  'wall-art': {
    label: 'Wall Art',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'colors',
    colorCodeLabel: 'Color',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Canvas, wood panel, or poster paper',
      `Acrylic paint or mixed media in ${colors} colors`,
      'Brushes in various sizes',
      'Palette and water cup',
      'Pencil for sketching',
      'Varnish or sealant'
    ],
    instructions: 'Sketch design lightly on surface. Block in large color areas first, then add details. Allow layers to dry between applications. Add highlights and shadows last. Seal with varnish when fully dry.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color palette guide',
      '✓ Complete materials list',
      '✓ Technique recommendations',
      '✓ Step-by-step painting order',
      '✓ Hanging and display tips'
    ],
    sizeCalc: () => `Design template — scale to desired frame size`,
  },

  'shadow-box': {
    label: 'Shadow Box',
    isGridBased: false,
    unitLabel: 'layers',
    colorLabel: 'paper/material colors',
    colorCodeLabel: 'Material',
    sizeIcon: 'layers',
    materials: (w, h, colors) => [
      `Cardstock or specialty paper in ${colors} colors`,
      'Shadow box frame (8x10" or desired size)',
      'Craft knife and cutting mat',
      'Foam mounting squares or spacers',
      'Glue (acid-free for longevity)',
      'LED strip or fairy lights (optional)',
      'Ruler and bone folder'
    ],
    instructions: 'Cut each layer from cardstock using templates. Score and fold any 3D elements. Stack layers from back to front with foam spacers between each for depth. Secure in shadow box frame. Add lighting behind back layer if desired.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Layer separation templates',
      '✓ Color and material guide',
      '✓ Complete materials list',
      '✓ Assembly order with spacing guide',
      '✓ Lighting and display tips'
    ],
    sizeCalc: () => `Design template — sized for standard shadow box frames`,
  },

  'block-printing': {
    label: 'Block Printing',
    isGridBased: false,
    unitLabel: 'blocks',
    colorLabel: 'ink colors',
    colorCodeLabel: 'Ink',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Linoleum block or rubber carving block',
      'Carving tools (V-gouge, U-gouge)',
      `Block printing ink in ${colors} colors`,
      'Brayer (roller)',
      'Printing surface (paper, fabric, etc.)',
      'Baren or wooden spoon',
      'Pencil and carbon paper for transfer'
    ],
    instructions: 'Transfer design to block (remember: image prints in REVERSE). Carve away areas that should NOT print. Roll ink onto block evenly with brayer. Press paper onto block and burnish with baren. Peel carefully. Allow to dry.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Reversed carving template',
      '✓ Ink color specifications',
      '✓ Complete materials list',
      '✓ Carving technique guide',
      '✓ Printing instructions'
    ],
    sizeCalc: () => `Design template — print at desired block size`,
  },

  'paper-quilling': {
    label: 'Paper Quilling',
    isGridBased: false,
    unitLabel: 'coils',
    colorLabel: 'paper colors',
    colorCodeLabel: 'Paper Strip',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Quilling paper strips in ${colors} colors (1/8" or 3mm width)`,
      'Quilling needle or slotted tool',
      'Cork board and pins',
      'PVA glue and fine-tip applicator',
      'Tweezers',
      'Circle template board',
      'Scissors',
      'Background paper or card'
    ],
    instructions: 'Roll paper strips into coils using quilling tool. Shape coils into teardrop, marquise, square, and other forms. Pin shapes on cork board following design layout. Glue shapes together and to background. Allow to dry completely before framing.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Shape and color placement guide',
      '✓ Coil type reference chart',
      '✓ Complete materials list',
      '✓ Step-by-step assembly guide',
      '✓ Framing and display tips'
    ],
    sizeCalc: () => `Design template — scale as desired`,
  },

  'fabric-painting': {
    label: 'Fabric Painting',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'paint colors',
    colorCodeLabel: 'Paint',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Pre-washed fabric item or yardage',
      `Fabric paint in ${colors} colors`,
      'Brushes (flat, round, detail)',
      'Fabric medium (if using acrylics)',
      'Palette and water cup',
      'Cardboard (inside garment to prevent bleed-through)',
      'Fabric marker for tracing design',
      'Iron (for heat-setting)'
    ],
    instructions: 'Pre-wash fabric without softener. Place cardboard behind painting area. Transfer or sketch design. Paint from light to dark colors. Allow to dry 24 hours. Heat-set with iron (medium heat, no steam, 3-5 minutes) or tumble dry on high.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color palette guide',
      '✓ Complete materials list',
      '✓ Painting technique tips',
      '✓ Heat-setting instructions',
      '✓ Care and washing guide'
    ],
    sizeCalc: () => `Design template — transfer at desired size`,
  },

  'batik': {
    label: 'Batik',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'dye colors',
    colorCodeLabel: 'Dye',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Natural fiber fabric (cotton, silk, or rayon)',
      'Beeswax and paraffin wax blend',
      'Tjanting tool or stamps',
      `Fiber-reactive dye in ${colors} colors`,
      'Wax melting pot',
      'Dye buckets',
      'Rubber gloves',
      'Iron and newspaper (for wax removal)',
      'Soda ash (for dye fixative)'
    ],
    instructions: 'Sketch design on fabric. Heat wax and apply to areas that should RESIST the first (lightest) dye color. Dye fabric. Re-wax areas to protect that color. Dye next color. Repeat for each color layer (light to dark). Remove wax by ironing between newspaper layers.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Wax resist sequence guide',
      '✓ Dye color order',
      '✓ Complete materials list',
      '✓ Waxing and dyeing instructions',
      '✓ Wax removal and finishing guide'
    ],
    sizeCalc: () => `Design template — scale to fabric size`,
  },

  'tie-dye': {
    label: 'Tie-Dye',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'dye colors',
    colorCodeLabel: 'Dye',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Pre-washed 100% cotton garment or fabric',
      `Fiber-reactive dye in ${colors} colors`,
      'Soda ash (sodium carbonate)',
      'Squeeze bottles',
      'Rubber bands or sinew',
      'Rubber gloves',
      'Plastic wrap and plastic bags',
      'Wire rack and tray'
    ],
    instructions: 'Soak fabric in soda ash solution for 20 minutes. Fold, twist, or scrunch fabric in desired pattern. Secure with rubber bands. Apply dye colors in sections as shown. Wrap in plastic and let cure 12-24 hours. Rinse from dark to light until water runs clear. Wash separately first time.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Fold/tie technique diagram',
      '✓ Dye color placement guide',
      '✓ Complete materials list',
      '✓ Step-by-step dyeing instructions',
      '✓ Curing and care guide'
    ],
    sizeCalc: () => `Design reference — results vary with fabric size and folding`,
  },

  // =========================================================================
  //  MISC / SPECIALTY CRAFTS (design-based)
  // =========================================================================

  'stencil': {
    label: 'Stencil',
    isGridBased: false,
    unitLabel: 'layers',
    colorLabel: 'colors',
    colorCodeLabel: 'Color',
    sizeIcon: 'layers',
    materials: (w, h, colors) => [
      'Mylar sheets or stencil material',
      'Craft knife and cutting mat',
      `Paint or ink in ${colors} colors`,
      'Stencil brush or sponge',
      'Tape for positioning',
      'Surface to stencil on'
    ],
    instructions: 'Cut stencil from mylar following the design template. Secure stencil to surface with tape. Apply paint with dabbing motion (not brushing). Remove stencil while paint is still wet for clean edges. For multi-layer stencils, allow each color to dry before positioning next layer.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Layer cutting templates',
      '✓ Color and layer order',
      '✓ Complete materials list',
      '✓ Application technique guide',
      '✓ Tips for clean edges'
    ],
    sizeCalc: () => `Stencil template — print at desired size`,
  },

  'window-cling': {
    label: 'Window Cling',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'paint colors',
    colorCodeLabel: 'Gallery Glass',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Gallery Glass window color paints',
      `${colors} colors plus black leading`,
      'Plastic sheet protector or acetate',
      'Pattern template',
      'Toothpick for detail work',
      'Flat drying surface'
    ],
    instructions: 'Place pattern under plastic sheet. Outline all sections with black liquid leading. Let leading dry completely (8+ hours). Fill sections with Gallery Glass colors. Use toothpick to push paint into corners. Dry flat 24 hours. Peel and apply to window.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color placement guide',
      '✓ Complete materials list',
      '✓ Leading and filling instructions',
      '✓ Drying and curing times',
      '✓ Application and removal tips'
    ],
    sizeCalc: () => `Design template — print to fit your window`,
  },

  'cake-decorating': {
    label: 'Cake Decorating',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'icing colors',
    colorCodeLabel: 'Color',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Prepared cake (iced with base coat)',
      `Gel food coloring in ${colors} colors`,
      'Buttercream or royal icing',
      'Piping bags and tips (round, star, leaf, petal)',
      'Offset spatula',
      'Fondant (optional)',
      'Turntable',
      'Bench scraper'
    ],
    instructions: 'Prepare and crumb-coat cake. Apply final coat of icing. Transfer design using parchment paper template or freehand. Pipe details from background to foreground. Add fondant elements if applicable. Refrigerate to set between layers.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color mixing guide',
      '✓ Piping tip recommendations',
      '✓ Complete materials list',
      '✓ Decorating sequence guide',
      '✓ Storage and serving tips'
    ],
    sizeCalc: () => `Design reference — adapt to your cake size`,
  },

  'pottery-painting': {
    label: 'Pottery Painting',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'glaze colors',
    colorCodeLabel: 'Glaze',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Bisque pottery piece',
      `Underglaze or glaze in ${colors} colors`,
      'Fine-tip brushes and detail brushes',
      'Palette and water cup',
      'Pencil for sketching on bisque',
      'Clear glaze for final coat',
      'Kiln access (or use at paint-your-own studio)'
    ],
    instructions: 'Sketch design lightly on bisque surface. Apply 2-3 coats of underglaze per section (must dry between coats). Apply clear glaze over entire piece. Fire in kiln according to glaze specifications.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Glaze color specifications',
      '✓ Complete materials list',
      '✓ Brush technique guide',
      '✓ Layering and firing instructions',
      '✓ Finishing tips'
    ],
    sizeCalc: () => `Design reference — adapt to your pottery piece`,
  },

  'decoupage': {
    label: 'Decoupage',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'paper types',
    colorCodeLabel: 'Paper',
    sizeIcon: 'layers',
    materials: (w, h, colors) => [
      'Surface (wood, glass, furniture, etc.)',
      'Decorative papers or napkins',
      'Mod Podge or decoupage medium',
      'Foam brush or flat brush',
      'Scissors and/or craft knife',
      'Sandpaper (for wood prep)',
      'Brayer or squeegee',
      'Sealant (polyurethane or varnish)'
    ],
    instructions: 'Prepare surface (sand, clean, prime if needed). Cut paper elements to size. Apply thin layer of medium to surface, position paper, smooth from center outward to remove bubbles. Apply 2-3 topcoats of medium, allowing dry time between each. Sand lightly between coats for smooth finish.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Element placement guide',
      '✓ Paper and material list',
      '✓ Complete materials list',
      '✓ Layering and adhesion instructions',
      '✓ Sealing and finishing guide'
    ],
    sizeCalc: () => `Design reference — scale elements to your surface`,
  },

  'leather-tooling': {
    label: 'Leather Tooling',
    isGridBased: false,
    unitLabel: 'sections',
    colorLabel: 'dye/finish colors',
    colorCodeLabel: 'Dye',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Vegetable-tanned leather (4-6 oz weight)',
      'Swivel knife',
      'Leather stamps (beveler, shader, backgrounder)',
      'Mallet or maul',
      'Modeling spoon',
      `Leather dye/stain in ${colors} colors`,
      'Sponge for casing leather',
      'Leather finish/sealant',
      'Pattern template and tracing film'
    ],
    instructions: 'Case (dampen) leather evenly. Transfer pattern using tracing film and stylus. Cut outlines with swivel knife. Stamp texture and depth using bevelers and shaders. Allow to dry. Apply dye in thin layers. Seal with leather finish.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Tooling sequence guide',
      '✓ Stamp selection reference',
      '✓ Complete materials list',
      '✓ Dyeing and finishing instructions',
      '✓ Leather care tips'
    ],
    sizeCalc: () => `Design template — print at full size for tracing`,
  },

  'door-hanger': {
    label: 'Door Hanger',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'colors',
    colorCodeLabel: 'Paint/Vinyl',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'MDF or wood blank (door hanger shape)',
      `Acrylic paint in ${colors} colors`,
      'Paint brushes',
      'Vinyl lettering or stencils (optional)',
      'Ribbon or twine for hanging',
      'Clear sealant',
      'Sandpaper'
    ],
    instructions: 'Sand blank smooth. Apply base coat and let dry. Paint design elements. Add lettering via vinyl, stencil, or hand-painting. Apply 2 coats of sealant. Attach ribbon/twine hanger. Optional: add bow or embellishments.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color palette guide',
      '✓ Complete materials list',
      '✓ Painting sequence guide',
      '✓ Lettering options',
      '✓ Finishing and sealing instructions'
    ],
    sizeCalc: () => `Design template — standard door hanger size`,
  },

  'jewelry-design': {
    label: 'Jewelry Design',
    isGridBased: false,
    unitLabel: 'components',
    colorLabel: 'material colors',
    colorCodeLabel: 'Material',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Wire, chain, or stringing material',
      `Beads/stones/findings in ${colors} colors`,
      'Jewelry pliers (round nose, flat nose, wire cutters)',
      'Clasps and jump rings',
      'Crimps and crimp covers',
      'Bead board or design mat',
      'Ruler/calipers'
    ],
    instructions: 'Lay out design on bead board. Measure and cut stringing material with extra length for finishing. String components in order shown. Attach clasp and crimp securely. Test closure and adjust length if needed.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Component list with quantities',
      '✓ Stringing/assembly order',
      '✓ Complete materials list',
      '✓ Technique reference (wirework, stringing)',
      '✓ Sizing and finishing guide'
    ],
    sizeCalc: () => `Design reference — adjust length to fit`,
  },

  'origami': {
    label: 'Origami',
    isGridBased: false,
    unitLabel: 'folds',
    colorLabel: 'paper colors',
    colorCodeLabel: 'Paper',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Origami paper in ${colors} colors (6" square traditional)`,
      'Bone folder for crisp folds',
      'Ruler (optional for precision)',
      'Glue (for modular origami only)'
    ],
    instructions: 'Start with paper color-side down unless noted. Make precise folds aligned to edges and center points. Crease firmly with bone folder. Follow numbered folding sequence exactly.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Paper size and color guide',
      '✓ Complete materials list',
      '✓ Step-by-step folding diagrams',
      '✓ Crease pattern reference',
      '✓ Tips for crisp results'
    ],
    sizeCalc: () => `Design reference — traditional 6" paper or scale`,
  },

  'scrapbook': {
    label: 'Scrapbook Layout',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'paper/embellishment colors',
    colorCodeLabel: 'Material',
    sizeIcon: 'layers',
    materials: (w, h, colors) => [
      `Cardstock and patterned paper in ${colors} colors`,
      'Photos',
      'Adhesive (tape runner, glue dots)',
      'Die cuts, stickers, or embellishments',
      'Paper trimmer or scissors',
      'Alphabet stamps or letter stickers',
      'Journaling pen'
    ],
    instructions: 'Cut background and mat papers to size. Arrange photos and elements following the layout design. Adhere background layers first, then photos, then embellishments. Add title and journaling. Use dimensional adhesive for layered elements.',
    pdfIncludes: [
      '✓ Full-size layout reference image',
      '✓ Paper and color guide',
      '✓ Element placement template',
      '✓ Complete materials list',
      '✓ Assembly order',
      '✓ Journaling prompts'
    ],
    sizeCalc: () => `12" x 12" standard scrapbook page`,
  },

  'candle-wrap': {
    label: 'Candle Wrap',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'colors',
    colorCodeLabel: 'Color',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Pillar candle or candle holder',
      'Tissue paper or rice paper',
      'Heat gun or hair dryer',
      `Embellishments in ${colors} colors`,
      'Ribbon or twine',
      'Scissors and ruler'
    ],
    instructions: 'Measure candle circumference and height. Cut design elements to fit. Wrap tissue paper around candle and apply gentle heat to adhere. Add embellishments. Tie with ribbon if desired. For safety: use LED candles or ensure flames stay below decoration level.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Sizing and measurement guide',
      '✓ Complete materials list',
      '✓ Application instructions',
      '✓ Safety guidelines',
      '✓ Display ideas'
    ],
    sizeCalc: () => `Design template — measure your candle to size`,
  },

  'nail-art': {
    label: 'Nail Art',
    isGridBased: false,
    unitLabel: 'nails',
    colorLabel: 'polish colors',
    colorCodeLabel: 'Polish',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Base coat',
      `Nail polish in ${colors} colors`,
      'Top coat',
      'Thin nail art brushes',
      'Dotting tools',
      'Nail art tape/stickers (optional)',
      'Acetone and cleanup brush',
      'Nail art stamping kit (optional)'
    ],
    instructions: 'Apply base coat and let dry. Apply 2 coats of base color. Create design elements using thin brushes and dotting tools as shown. Allow each layer to dry before adding the next. Seal with top coat.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Nail-by-nail design guide',
      '✓ Polish color specifications',
      '✓ Complete materials list',
      '✓ Technique tutorials',
      '✓ Application order per nail'
    ],
    sizeCalc: () => `Design reference — scaled to nail size`,
  },

  'miniature': {
    label: 'Miniature',
    isGridBased: false,
    unitLabel: 'components',
    colorLabel: 'paint colors',
    colorCodeLabel: 'Paint',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Miniature kit or materials (wood, clay, resin)',
      `Acrylic paint in ${colors} colors`,
      'Fine detail brushes (sizes 0, 00, 000)',
      'Craft knife and cutting mat',
      'Tweezers',
      'Magnifying glass or lamp',
      'PVA and super glue',
      'Primer and sealant'
    ],
    instructions: 'Assemble components if needed. Prime all surfaces. Paint from large areas to small details, base coats first. Use thin layers and allow drying between coats. Add washes for depth and dry-brush highlights. Seal finished piece.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Component and paint list',
      '✓ Color mixing guide',
      '✓ Complete materials list',
      '✓ Assembly instructions',
      '✓ Painting technique tips'
    ],
    sizeCalc: () => `Design reference — scaled to miniature ratio`,
  },

  'seamless-tile': {
    label: 'Seamless Pattern',
    isGridBased: false,
    unitLabel: 'repeat unit',
    colorLabel: 'colors',
    colorCodeLabel: 'Color',
    sizeIcon: 'grid',
    materials: (w, h, colors) => [
      'Digital design software',
      `${colors} color palette`,
      'Printer and appropriate material (if physical use)'
    ],
    instructions: 'This seamless tile is designed to repeat perfectly in all directions. Import into your design software and tile as needed. For fabric printing, upload to a print-on-demand service. For digital use, set as repeating background.',
    pdfIncludes: [
      '✓ Full-size pattern tile image',
      '✓ Color palette with values',
      '✓ Repeat preview (2x2 tiled)',
      '✓ Usage recommendations',
      '✓ Print specifications'
    ],
    sizeCalc: () => `Single repeat tile — tiles seamlessly in all directions`,
  },

  'book-nook': {
    label: 'Book Nook',
    isGridBased: false,
    unitLabel: 'components',
    colorLabel: 'material colors',
    colorCodeLabel: 'Material',
    sizeIcon: 'layers',
    materials: (w, h, colors) => [
      'Foam board or balsa wood',
      `Cardstock/paper in ${colors} colors`,
      'LED strip lights or fairy lights',
      'Craft knife and cutting mat',
      'PVA glue and hot glue',
      'Paint and brushes',
      'Miniature props and details',
      'Clear acetate (for windows)'
    ],
    instructions: 'Cut side panels, floor, and ceiling from foam board. Create backdrop layers for depth. Add lighting behind back panel. Assemble structure with hot glue. Add details, props, and miniature elements. Final touchups and weathering.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Component templates',
      '✓ Layer/depth guide',
      '✓ Complete materials list',
      '✓ Assembly sequence',
      '✓ Lighting and finishing tips'
    ],
    sizeCalc: () => `Standard bookshelf insert size (varies)`,
  },

  'badge-button': {
    label: 'Button/Badge',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'colors',
    colorCodeLabel: 'Color',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Button/badge maker machine',
      'Button blanks (1", 1.5", or 2.25")',
      'Printed design on paper',
      'Circle punch or cutter',
      'Clear mylar circles'
    ],
    instructions: 'Print design at correct size for your button maker. Cut circle with appropriate margin for wrap. Assemble in button maker: shell, design, mylar, back. Press firmly.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Print-ready circle template',
      '✓ Color specifications',
      '✓ Complete materials list',
      '✓ Assembly instructions',
      '✓ Printing tips for best results'
    ],
    sizeCalc: () => `Design template — sized for standard button blanks`,
  },

  'magnet-design': {
    label: 'Magnet Design',
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'colors',
    colorCodeLabel: 'Color',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      'Magnetic sheet or individual magnets',
      'Printed design or painted surface',
      'Laminating pouch or clear sealant',
      'Scissors or craft knife',
      'Adhesive (if using separate magnet backing)'
    ],
    instructions: 'Print or create design on magnetic sheet. Cut to shape. For non-magnetic surfaces: create design, seal/laminate, and attach magnet backing with adhesive.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color specifications',
      '✓ Complete materials list',
      '✓ Cutting and assembly guide',
      '✓ Sealing options',
      '✓ Size templates'
    ],
    sizeCalc: () => `Design template — print at desired magnet size`,
  },

  'terrarium': {
    label: 'Terrarium',
    isGridBased: false,
    unitLabel: 'layers',
    colorLabel: 'material types',
    colorCodeLabel: 'Material',
    sizeIcon: 'layers',
    materials: (w, h, colors) => [
      'Glass container (open or closed)',
      'Pebbles/gravel for drainage',
      'Activated charcoal',
      'Sphagnum moss',
      'Potting soil mix',
      `Plants (${colors} varieties as shown)`,
      'Decorative elements (stones, figurines)',
      'Spray bottle',
      'Long tweezers and spoon'
    ],
    instructions: 'Layer from bottom: pebbles (1"), charcoal (thin layer), moss (barrier), soil (2-3"). Plant largest plant first, then fill in. Add decorative elements. Mist lightly. For closed terrariums: seal and monitor moisture. For open: water sparingly.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Layer diagram',
      '✓ Plant placement guide',
      '✓ Complete materials list',
      '✓ Assembly instructions',
      '✓ Care and maintenance guide'
    ],
    sizeCalc: () => `Design reference — adapt to your container size`,
  },
}


// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getCraftConfig(craftType) {
  if (CRAFT_CONFIGS[craftType]) return CRAFT_CONFIGS[craftType]
  
  // Smart fallback: check if the craft name contains keywords
  const ct = (craftType || '').toLowerCase()
  if (ct.includes('stitch') || ct.includes('counted')) return CRAFT_CONFIGS['cross-stitch']
  if (ct.includes('knit')) return CRAFT_CONFIGS['knitting']
  if (ct.includes('crochet') && (ct.includes('chart') || ct.includes('filet') || ct.includes('tapestry') || ct.includes('c2c') || ct.includes('corner'))) return CRAFT_CONFIGS['crochet-chart']
  if (ct.includes('crochet')) return CRAFT_CONFIGS['crochet']
  if (ct.includes('glass') && ct.includes('stain')) return CRAFT_CONFIGS['stained-glass']
  if (ct.includes('glass') && ct.includes('etch')) return CRAFT_CONFIGS['glass-etching']
  if (ct.includes('bead') && ct.includes('perler')) return CRAFT_CONFIGS['perler-beads']
  if (ct.includes('bead')) return CRAFT_CONFIGS['beading']
  if (ct.includes('quilt')) return CRAFT_CONFIGS['quilting']
  if (ct.includes('embroider')) return CRAFT_CONFIGS['embroidery']
  if (ct.includes('vinyl')) return CRAFT_CONFIGS['vinyl-decal']
  if (ct.includes('sublim')) return CRAFT_CONFIGS['sublimation']
  if (ct.includes('punch')) return CRAFT_CONFIGS['punch-needle']
  if (ct.includes('latch')) return CRAFT_CONFIGS['latch-hook']
  if (ct.includes('diamond')) return CRAFT_CONFIGS['diamond-painting']
  if (ct.includes('mosaic')) return CRAFT_CONFIGS['mosaic']
  if (ct.includes('macram')) return CRAFT_CONFIGS['macrame']
  if (ct.includes('sew')) return CRAFT_CONFIGS['sewing']
  if (ct.includes('leather')) return CRAFT_CONFIGS['leather-tooling']
  if (ct.includes('wood') && ct.includes('burn')) return CRAFT_CONFIGS['wood-burning']
  if (ct.includes('laser')) return CRAFT_CONFIGS['laser-engraving']
  if (ct.includes('screen') && ct.includes('print')) return CRAFT_CONFIGS['screen-printing']
  if (ct.includes('resin')) return CRAFT_CONFIGS['resin-art']
  if (ct.includes('candle')) return CRAFT_CONFIGS['candle-wrap']
  if (ct.includes('scroll')) return CRAFT_CONFIGS['scroll-saw']
  
  // Generic design-based fallback for truly unknown types
  return {
    label: (craftType || 'Craft').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    isGridBased: false,
    unitLabel: 'elements',
    colorLabel: 'colors',
    colorCodeLabel: 'Color',
    sizeIcon: 'ruler',
    materials: (w, h, colors) => [
      `Materials in ${colors} colors as needed for this craft`,
      'See design reference for specific requirements'
    ],
    instructions: 'Follow the design reference image. Use the color palette as a guide for material selection. Adapt techniques to your skill level and available tools.',
    pdfIncludes: [
      '✓ Full-size design reference image',
      '✓ Color palette guide',
      '✓ Complete materials list',
      '✓ Step-by-step instructions'
    ],
    sizeCalc: () => `Design template — scale to desired size`,
  }
}

function formatCraftLabel(craftType) {
  const config = CRAFT_CONFIGS[craftType]
  if (config) return config.label
  return (craftType || 'Craft').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}


// =============================================================================
// DMC THREAD COLOR DATABASE
// =============================================================================
const DMC_COLORS = [
  { code: 'DMC 310', name: 'Black', r: 0, g: 0, b: 0 },
  { code: 'DMC B5200', name: 'Snow White', r: 255, g: 255, b: 255 },
  { code: 'DMC 666', name: 'Bright Red', r: 227, g: 29, b: 39 },
  { code: 'DMC 321', name: 'Christmas Red', r: 199, g: 43, b: 59 },
  { code: 'DMC 498', name: 'Dark Red', r: 167, g: 19, b: 43 },
  { code: 'DMC 816', name: 'Garnet', r: 151, g: 11, b: 35 },
  { code: 'DMC 304', name: 'Medium Red', r: 183, g: 31, b: 51 },
  { code: 'DMC 349', name: 'Dark Coral', r: 210, g: 16, b: 53 },
  { code: 'DMC 817', name: 'Very Dark Coral', r: 187, g: 5, b: 31 },
  { code: 'DMC 3801', name: 'Melon', r: 231, g: 73, b: 103 },
  { code: 'DMC 956', name: 'Geranium', r: 255, g: 145, b: 145 },
  { code: 'DMC 957', name: 'Pale Geranium', r: 253, g: 181, b: 181 },
  { code: 'DMC 760', name: 'Salmon', r: 245, g: 173, b: 173 },
  { code: 'DMC 761', name: 'Light Salmon', r: 255, g: 201, b: 201 },
  { code: 'DMC 353', name: 'Peach', r: 254, g: 215, b: 204 },
  { code: 'DMC 352', name: 'Light Coral', r: 253, g: 156, b: 151 },
  { code: 'DMC 351', name: 'Coral', r: 233, g: 106, b: 103 },
  { code: 'DMC 350', name: 'Medium Coral', r: 224, g: 72, b: 72 },
  { code: 'DMC 608', name: 'Bright Orange', r: 253, g: 93, b: 53 },
  { code: 'DMC 606', name: 'Orange Red', r: 250, g: 50, b: 3 },
  { code: 'DMC 740', name: 'Tangerine', r: 255, g: 139, b: 0 },
  { code: 'DMC 741', name: 'Medium Tangerine', r: 255, g: 163, b: 43 },
  { code: 'DMC 742', name: 'Light Tangerine', r: 255, g: 191, b: 87 },
  { code: 'DMC 743', name: 'Yellow', r: 255, g: 214, b: 92 },
  { code: 'DMC 744', name: 'Pale Yellow', r: 255, g: 231, b: 147 },
  { code: 'DMC 745', name: 'Light Pale Yellow', r: 255, g: 240, b: 179 },
  { code: 'DMC 307', name: 'Lemon', r: 253, g: 237, b: 84 },
  { code: 'DMC 973', name: 'Bright Canary', r: 255, g: 227, b: 0 },
  { code: 'DMC 444', name: 'Dark Lemon', r: 255, g: 214, b: 0 },
  { code: 'DMC 907', name: 'Light Parrot Green', r: 199, g: 230, b: 102 },
  { code: 'DMC 906', name: 'Medium Parrot Green', r: 127, g: 179, b: 53 },
  { code: 'DMC 905', name: 'Dark Parrot Green', r: 98, g: 138, b: 40 },
  { code: 'DMC 704', name: 'Bright Chartreuse', r: 158, g: 207, b: 52 },
  { code: 'DMC 702', name: 'Kelly Green', r: 71, g: 167, b: 47 },
  { code: 'DMC 700', name: 'Bright Green', r: 7, g: 115, b: 27 },
  { code: 'DMC 699', name: 'Christmas Green', r: 5, g: 101, b: 23 },
  { code: 'DMC 909', name: 'Very Dark Emerald Green', r: 21, g: 111, b: 73 },
  { code: 'DMC 910', name: 'Dark Emerald Green', r: 24, g: 126, b: 86 },
  { code: 'DMC 911', name: 'Medium Emerald Green', r: 24, g: 144, b: 101 },
  { code: 'DMC 912', name: 'Light Emerald Green', r: 27, g: 157, b: 107 },
  { code: 'DMC 913', name: 'Medium Nile Green', r: 109, g: 171, b: 119 },
  { code: 'DMC 955', name: 'Light Nile Green', r: 162, g: 214, b: 173 },
  { code: 'DMC 954', name: 'Nile Green', r: 136, g: 186, b: 145 },
  { code: 'DMC 943', name: 'Medium Aquamarine', r: 61, g: 147, b: 132 },
  { code: 'DMC 3812', name: 'Very Dark Seagreen', r: 47, g: 140, b: 132 },
  { code: 'DMC 807', name: 'Peacock Blue', r: 100, g: 171, b: 186 },
  { code: 'DMC 806', name: 'Dark Peacock Blue', r: 61, g: 149, b: 165 },
  { code: 'DMC 3765', name: 'Very Dark Peacock Blue', r: 52, g: 127, b: 140 },
  { code: 'DMC 3766', name: 'Light Peacock Blue', r: 153, g: 207, b: 217 },
  { code: 'DMC 598', name: 'Light Turquoise', r: 144, g: 195, b: 204 },
  { code: 'DMC 597', name: 'Turquoise', r: 91, g: 163, b: 179 },
  { code: 'DMC 3810', name: 'Dark Turquoise', r: 72, g: 142, b: 154 },
  { code: 'DMC 3809', name: 'Very Dark Turquoise', r: 63, g: 124, b: 133 },
  { code: 'DMC 3808', name: 'Ultra Very Dark Turquoise', r: 54, g: 105, b: 112 },
  { code: 'DMC 519', name: 'Sky Blue', r: 126, g: 177, b: 200 },
  { code: 'DMC 518', name: 'Light Wedgwood', r: 79, g: 147, b: 167 },
  { code: 'DMC 517', name: 'Dark Wedgwood', r: 59, g: 118, b: 143 },
  { code: 'DMC 3761', name: 'Light Sky Blue', r: 172, g: 216, b: 226 },
  { code: 'DMC 3325', name: 'Light Baby Blue', r: 184, g: 210, b: 230 },
  { code: 'DMC 334', name: 'Medium Baby Blue', r: 115, g: 159, b: 193 },
  { code: 'DMC 322', name: 'Dark Baby Blue', r: 90, g: 143, b: 184 },
  { code: 'DMC 312', name: 'Very Dark Baby Blue', r: 53, g: 102, b: 139 },
  { code: 'DMC 311', name: 'Medium Navy Blue', r: 28, g: 80, b: 102 },
  { code: 'DMC 336', name: 'Navy Blue', r: 36, g: 73, b: 103 },
  { code: 'DMC 823', name: 'Dark Navy Blue', r: 33, g: 48, b: 99 },
  { code: 'DMC 939', name: 'Very Dark Navy Blue', r: 27, g: 40, b: 83 },
  { code: 'DMC 3756', name: 'Ultra Very Light Baby Blue', r: 238, g: 252, b: 252 },
  { code: 'DMC 775', name: 'Very Light Baby Blue', r: 217, g: 235, b: 241 },
  { code: 'DMC 3841', name: 'Pale Baby Blue', r: 205, g: 223, b: 237 },
  { code: 'DMC 827', name: 'Very Light Blue', r: 189, g: 221, b: 237 },
  { code: 'DMC 813', name: 'Light Blue', r: 161, g: 194, b: 215 },
  { code: 'DMC 826', name: 'Medium Blue', r: 107, g: 158, b: 191 },
  { code: 'DMC 825', name: 'Dark Blue', r: 71, g: 129, b: 165 },
  { code: 'DMC 824', name: 'Very Dark Blue', r: 57, g: 105, b: 135 },
  { code: 'DMC 996', name: 'Medium Electric Blue', r: 48, g: 194, b: 236 },
  { code: 'DMC 995', name: 'Dark Electric Blue', r: 38, g: 150, b: 182 },
  { code: 'DMC 3843', name: 'Electric Blue', r: 20, g: 170, b: 208 },
  { code: 'DMC 3755', name: 'Baby Blue', r: 147, g: 180, b: 206 },
  { code: 'DMC 799', name: 'Medium Delft Blue', r: 116, g: 142, b: 182 },
  { code: 'DMC 798', name: 'Dark Delft Blue', r: 70, g: 106, b: 142 },
  { code: 'DMC 797', name: 'Royal Blue', r: 19, g: 71, b: 125 },
  { code: 'DMC 796', name: 'Dark Royal Blue', r: 17, g: 65, b: 109 },
  { code: 'DMC 820', name: 'Very Dark Royal Blue', r: 14, g: 54, b: 92 },
  { code: 'DMC 340', name: 'Medium Blue Violet', r: 173, g: 167, b: 199 },
  { code: 'DMC 341', name: 'Light Blue Violet', r: 183, g: 191, b: 221 },
  { code: 'DMC 333', name: 'Very Dark Blue Violet', r: 92, g: 84, b: 120 },
  { code: 'DMC 550', name: 'Very Dark Violet', r: 92, g: 24, b: 78 },
  { code: 'DMC 552', name: 'Medium Violet', r: 128, g: 58, b: 107 },
  { code: 'DMC 553', name: 'Violet', r: 163, g: 99, b: 139 },
  { code: 'DMC 554', name: 'Light Violet', r: 219, g: 179, b: 203 },
  { code: 'DMC 208', name: 'Very Dark Lavender', r: 131, g: 91, b: 139 },
  { code: 'DMC 209', name: 'Dark Lavender', r: 163, g: 123, b: 167 },
  { code: 'DMC 210', name: 'Medium Lavender', r: 195, g: 159, b: 195 },
  { code: 'DMC 211', name: 'Light Lavender', r: 227, g: 203, b: 227 },
  { code: 'DMC 718', name: 'Plum', r: 156, g: 36, b: 98 },
  { code: 'DMC 917', name: 'Medium Plum', r: 155, g: 19, b: 89 },
  { code: 'DMC 915', name: 'Dark Plum', r: 130, g: 0, b: 67 },
  { code: 'DMC 3607', name: 'Light Plum', r: 197, g: 73, b: 137 },
  { code: 'DMC 3608', name: 'Very Light Plum', r: 234, g: 156, b: 196 },
  { code: 'DMC 3609', name: 'Ultra Light Plum', r: 244, g: 174, b: 213 },
  { code: 'DMC 962', name: 'Medium Dusty Rose', r: 230, g: 138, b: 138 },
  { code: 'DMC 961', name: 'Dark Dusty Rose', r: 207, g: 115, b: 115 },
  { code: 'DMC 963', name: 'Ultra Very Light Dusty Rose', r: 255, g: 215, b: 215 },
  { code: 'DMC 892', name: 'Medium Carnation', r: 255, g: 121, b: 140 },
  { code: 'DMC 818', name: 'Baby Pink', r: 255, g: 223, b: 217 },
  { code: 'DMC 776', name: 'Medium Pink', r: 252, g: 176, b: 185 },
  { code: 'DMC 899', name: 'Medium Rose', r: 242, g: 118, b: 136 },
  { code: 'DMC 335', name: 'Rose', r: 238, g: 84, b: 110 },
  { code: 'DMC 948', name: 'Very Light Peach', r: 254, g: 231, b: 218 },
  { code: 'DMC 754', name: 'Light Peach', r: 247, g: 203, b: 191 },
  { code: 'DMC 758', name: 'Very Light Terra Cotta', r: 238, g: 170, b: 155 },
  { code: 'DMC 356', name: 'Medium Terra Cotta', r: 197, g: 106, b: 91 },
  { code: 'DMC 355', name: 'Dark Terra Cotta', r: 152, g: 68, b: 54 },
  { code: 'DMC 951', name: 'Light Tawny', r: 255, g: 226, b: 207 },
  { code: 'DMC 945', name: 'Tawny', r: 251, g: 213, b: 187 },
  { code: 'DMC 402', name: 'Very Light Mahogany', r: 247, g: 167, b: 119 },
  { code: 'DMC 301', name: 'Medium Mahogany', r: 179, g: 95, b: 43 },
  { code: 'DMC 400', name: 'Dark Mahogany', r: 143, g: 67, b: 15 },
  { code: 'DMC 977', name: 'Light Golden Brown', r: 220, g: 156, b: 86 },
  { code: 'DMC 976', name: 'Medium Golden Brown', r: 194, g: 129, b: 66 },
  { code: 'DMC 975', name: 'Dark Golden Brown', r: 145, g: 79, b: 18 },
  { code: 'DMC 922', name: 'Light Copper', r: 226, g: 115, b: 35 },
  { code: 'DMC 921', name: 'Copper', r: 198, g: 98, b: 24 },
  { code: 'DMC 920', name: 'Medium Copper', r: 172, g: 84, b: 20 },
  { code: 'DMC 738', name: 'Very Light Tan', r: 236, g: 204, b: 158 },
  { code: 'DMC 437', name: 'Light Tan', r: 228, g: 187, b: 142 },
  { code: 'DMC 436', name: 'Tan', r: 203, g: 144, b: 81 },
  { code: 'DMC 435', name: 'Very Light Brown', r: 184, g: 119, b: 72 },
  { code: 'DMC 434', name: 'Light Brown', r: 152, g: 94, b: 51 },
  { code: 'DMC 433', name: 'Medium Brown', r: 122, g: 69, b: 31 },
  { code: 'DMC 801', name: 'Dark Coffee Brown', r: 101, g: 57, b: 25 },
  { code: 'DMC 898', name: 'Very Dark Coffee Brown', r: 73, g: 42, b: 19 },
  { code: 'DMC 938', name: 'Ultra Dark Coffee Brown', r: 54, g: 31, b: 14 },
  { code: 'DMC 3371', name: 'Black Brown', r: 30, g: 17, b: 8 },
  { code: 'DMC 842', name: 'Very Light Beige Brown', r: 209, g: 186, b: 161 },
  { code: 'DMC 841', name: 'Light Beige Brown', r: 182, g: 155, b: 126 },
  { code: 'DMC 840', name: 'Medium Beige Brown', r: 154, g: 124, b: 92 },
  { code: 'DMC 839', name: 'Dark Beige Brown', r: 103, g: 76, b: 50 },
  { code: 'DMC 762', name: 'Very Light Pearl Gray', r: 236, g: 236, b: 236 },
  { code: 'DMC 415', name: 'Pearl Gray', r: 211, g: 211, b: 214 },
  { code: 'DMC 318', name: 'Light Steel Gray', r: 171, g: 171, b: 171 },
  { code: 'DMC 414', name: 'Dark Steel Gray', r: 140, g: 140, b: 140 },
  { code: 'DMC 317', name: 'Pewter Gray', r: 108, g: 108, b: 108 },
  { code: 'DMC 413', name: 'Dark Pewter Gray', r: 86, g: 86, b: 86 },
  { code: 'DMC 3799', name: 'Very Dark Pewter Gray', r: 66, g: 66, b: 66 },
  { code: 'DMC ECRU', name: 'Ecru', r: 240, g: 234, b: 218 },
  { code: 'DMC 746', name: 'Off White', r: 252, g: 252, b: 238 },
]

function findClosestDMC(r, g, b) {
  let closest = DMC_COLORS[0]
  let minDist = Infinity
  
  for (const dmc of DMC_COLORS) {
    const dist = Math.sqrt(
      Math.pow(r - dmc.r, 2) + 
      Math.pow(g - dmc.g, 2) + 
      Math.pow(b - dmc.b, 2)
    )
    if (dist < minDist) {
      minDist = dist
      closest = dmc
    }
  }
  
  return closest
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')
}


// =============================================================================
// COMPONENT
// =============================================================================
export default function PatternPdfModal({ isOpen, onClose, patternData, canvasImage, craftType: craftTypeProp, canvasImageUrl, projectName }) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [gridData, setGridData] = useState(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  
  // Resolve craft type: prop > patternData.type > fallback
  const craftType = craftTypeProp || patternData?.type || 'cross-stitch'
  const craftConfig = getCraftConfig(craftType)
  const craftLabel = formatCraftLabel(craftType)
  const isGrid = craftConfig.isGridBased
  
  // Resolve canvas image from either prop
  const resolvedCanvasImage = canvasImageUrl || canvasImage
  
  // Analyze image when modal opens — ONLY for grid-based crafts
  useEffect(() => {
    if (isOpen && resolvedCanvasImage && !gridData && isGrid) {
      analyzeImage()
    }
  }, [isOpen, resolvedCanvasImage])
  
  // For non-grid crafts, extract color palette from image (no grid)
  // BUT skip if backend already provided colors via symbolKey
  useEffect(() => {
    if (isOpen && !gridData && !isGrid) {
      const backendColors = patternData?.symbolKey
      const hasBackendColors = Array.isArray(backendColors) && backendColors.length > 0
      
      if (hasBackendColors) {
        console.log('[PatternPdfModal] Using backend-provided colors, skipping image extraction')
        return
      }
      
      if (resolvedCanvasImage) {
        extractColorPalette()
      }
    }
  }, [isOpen, resolvedCanvasImage])
  
  // Reset grid data when modal closes
  useEffect(() => {
    if (!isOpen) {
      setGridData(null)
    }
  }, [isOpen])
  
  // Extract just the color palette for non-grid crafts (no symbol grid)
  const extractColorPalette = async () => {
    if (!resolvedCanvasImage) return
    setIsAnalyzing(true)
    
    try {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
        img.src = resolvedCanvasImage
      })
      
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      // Sample at reasonable resolution for color extraction
      const sampleSize = 100
      canvas.width = sampleSize
      canvas.height = sampleSize
      ctx.drawImage(img, 0, 0, sampleSize, sampleSize)
      
      const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize)
      const pixels = imageData.data
      
      // Collect color frequencies
      const colorMap = new Map()
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2]
        // Quantize to reduce noise
        const qr = Math.round(r / 16) * 16
        const qg = Math.round(g / 16) * 16
        const qb = Math.round(b / 16) * 16
        const key = `${qr},${qg},${qb}`
        colorMap.set(key, (colorMap.get(key) || 0) + 1)
      }
      
      // Get top colors, map to nearest named colors
      const sorted = [...colorMap.entries()].sort((a, b) => b[1] - a[1])
      
      // Determine if this craft uses thread-based colors (DMC) or generic colors
      const threadCrafts = ['cross-stitch', 'embroidery', 'punch-needle', 'needlepoint', 'hardanger']
      const usesDMC = threadCrafts.includes(craftType)
      
      const topColors = sorted.slice(0, 20).map(([key, count], index) => {
        const [r, g, b] = key.split(',').map(Number)
        const dmc = findClosestDMC(r, g, b)
        
        if (usesDMC) {
          // Thread-based crafts: use DMC hex for swatch so it matches the name
          return {
            hex: rgbToHex(dmc.r, dmc.g, dmc.b),
            dmc: dmc,
            name: dmc.name,
            code: dmc.code,
            count: count,
            symbol: SYMBOLS[index]
          }
        } else {
          // Non-thread crafts (stained glass, vinyl, etc.): use DMC hex + generic names
          return {
            hex: rgbToHex(dmc.r, dmc.g, dmc.b),
            dmc: dmc,
            name: dmc.name,
            code: rgbToHex(dmc.r, dmc.g, dmc.b).toUpperCase(),
            count: count,
            symbol: SYMBOLS[index]
          }
        }
      })
      
      // Deduplicate by DMC code
      const seen = new Set()
      const deduped = topColors.filter(c => {
        if (seen.has(c.dmc.code)) return false
        seen.add(c.dmc.code)
        return true
      })
      
      setGridData({
        colors: deduped,
        width: patternData?.dimensions?.width || img.naturalWidth,
        height: patternData?.dimensions?.height || img.naturalHeight,
        isDesignBased: true
      })
      
    } catch (error) {
      console.error('Failed to extract color palette:', error)
    } finally {
      setIsAnalyzing(false)
    }
  }
  
  const analyzeImage = async () => {
    if (!resolvedCanvasImage) return
    
    setIsAnalyzing(true)
    
    // Determine if this craft uses DMC thread codes or generic color names
    const dmcGridCrafts = ['cross-stitch', 'embroidery', 'needlepoint', 'punch-needle', 'hardanger']
    const usesDMC = dmcGridCrafts.includes((craftType || '').toLowerCase())
    
    try {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
        img.src = resolvedCanvasImage
      })
      
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      const targetWidth = patternData?.dimensions?.width || 80
      const targetHeight = patternData?.dimensions?.height || 80
      
      canvas.width = targetWidth
      canvas.height = targetHeight
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight)
      
      const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight)
      const pixels = imageData.data
      
      const grid = []
      const colorMap = new Map()
      
      for (let y = 0; y < targetHeight; y++) {
        const row = []
        for (let x = 0; x < targetWidth; x++) {
          const i = (y * targetWidth + x) * 4
          const r = pixels[i]
          const g = pixels[i + 1]
          const b = pixels[i + 2]
          
          // Use DMC palette for color quantization (good palette regardless of craft)
          const dmc = findClosestDMC(r, g, b)
          const hex = rgbToHex(dmc.r, dmc.g, dmc.b)
          
          if (!colorMap.has(hex)) {
            if (usesDMC) {
              // Thread-based crafts: show DMC codes (e.g., "DMC 820")
              colorMap.set(hex, {
                count: 0,
                dmc: dmc,
                code: dmc.code,
                name: dmc.name,
                hex: hex,
                symbol: null
              })
            } else {
              // Non-thread crafts (mosaic, perler, knitting, etc.): show color names
              colorMap.set(hex, {
                count: 0,
                dmc: dmc,
                code: dmc.name,
                name: dmc.name,
                hex: hex,
                symbol: null
              })
            }
          }
          colorMap.get(hex).count++
          
          row.push(hex)
        }
        grid.push(row)
      }
      
      const sortedColors = [...colorMap.entries()]
        .sort((a, b) => b[1].count - a[1].count)
      
      sortedColors.forEach(([hex, data], index) => {
        data.symbol = SYMBOLS[index % SYMBOLS.length]
      })
      
      setGridData({
        grid,
        colors: sortedColors.map(([hex, data]) => data),
        width: targetWidth,
        height: targetHeight
      })
      
    } catch (error) {
      console.error('Failed to analyze image:', error)
    } finally {
      setIsAnalyzing(false)
    }
  }
  
  if (!isOpen || !patternData) return null

  // Safe array coercion — backend may return strings or objects instead of arrays
  const ensureArray = (val) => {
    if (Array.isArray(val)) return val
    if (val && typeof val === 'string') {
      // Try parsing JSON string
      try { const parsed = JSON.parse(val); if (Array.isArray(parsed)) return parsed } catch {}
      // Split comma-separated or newline-separated strings
      return val.split(/[,\n]/).map(s => s.trim()).filter(Boolean)
    }
    if (val && typeof val === 'object' && val.length != null) return Array.from(val)
    if (val && typeof val === 'object') return Object.values(val)
    return []
  }

  const {
    type,
    title: patternTitle,
    dimensions,
    finishedSize,
    difficulty,
    estimatedTime,
    symbolKey: rawSymbolKey,
    colorCount,
    materials: rawMaterials,
    instructions,
    generatedAt
  } = patternData
  
  const symbolKey = ensureArray(rawSymbolKey)
  const materials = ensureArray(rawMaterials)
  
  const title = projectName || patternTitle || 'Zenna Pattern'

  // =========================================================================
  // PDF GENERATION — branches for grid vs. design-based crafts
  // =========================================================================
  const generatePDF = async () => {
    setIsGenerating(true)
    
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })
      
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 10
      let y = 15

      // ===== PAGE 1: COVER & INFO (shared) =====
      
      doc.setFontSize(20)
      doc.setFont('helvetica', 'bold')
      doc.text(title, pageWidth / 2, y, { align: 'center' })
      y += 10

      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(100)
      doc.text(`${craftLabel.toUpperCase()} PATTERN`, pageWidth / 2, y, { align: 'center' })
      doc.setTextColor(0)
      y += 12

      // Pattern Info Box
      doc.setDrawColor(200)
      doc.setFillColor(248, 250, 252)
      doc.roundedRect(margin, y, pageWidth - margin * 2, 28, 2, 2, 'FD')
      y += 8

      doc.setFontSize(9)
      const col1 = margin + 5
      const col2 = pageWidth / 2 + 5

      const actualWidth = gridData?.width || dimensions?.width || 80
      const actualHeight = gridData?.height || dimensions?.height || 80
      const actualColors = gridData?.colors?.length || colorCount || 12

      // Row 1 of info box
      doc.setFont('helvetica', 'bold')
      if (isGrid) {
        doc.text('Size:', col1, y)
        doc.setFont('helvetica', 'normal')
        doc.text(`${actualWidth} x ${actualHeight} ${craftConfig.unitLabel}`, col1 + 18, y)
      } else {
        doc.text('Type:', col1, y)
        doc.setFont('helvetica', 'normal')
        doc.text(craftLabel, col1 + 18, y)
      }

      doc.setFont('helvetica', 'bold')
      doc.text('Difficulty:', col2, y)
      doc.setFont('helvetica', 'normal')
      doc.text((difficulty || 'Intermediate').charAt(0).toUpperCase() + (difficulty || 'intermediate').slice(1), col2 + 22, y)
      y += 6

      // Row 2
      doc.setFont('helvetica', 'bold')
      doc.text('Finished:', col1, y)
      doc.setFont('helvetica', 'normal')
      const finSize = finishedSize || craftConfig.sizeCalc(actualWidth, actualHeight)
      doc.text(finSize, col1 + 20, y)

      doc.setFont('helvetica', 'bold')
      doc.text('Est. Time:', col2, y)
      doc.setFont('helvetica', 'normal')
      doc.text(estimatedTime || '8-16 hours', col2 + 22, y)
      y += 6

      // Row 3
      doc.setFont('helvetica', 'bold')
      doc.text('Colors:', col1, y)
      doc.setFont('helvetica', 'normal')
      doc.text(`${actualColors} ${craftConfig.colorLabel}`, col1 + 18, y)

      if (isGrid) {
        doc.setFont('helvetica', 'bold')
        doc.text(`Total ${craftConfig.unitLabel}:`, col2, y)
        doc.setFont('helvetica', 'normal')
        doc.text(`${(actualWidth * actualHeight).toLocaleString()}`, col2 + 25, y)
      }
      y += 18

      // Color Key Section
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Color Palette', margin, y)
      y += 6

      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      if (isGrid) {
        doc.text('Symbol', margin, y)
        doc.text('Color Name', margin + 20, y)
      } else {
        doc.text('Swatch', margin, y)
        doc.text('Color Name', margin + 20, y)
      }
      doc.text(craftConfig.colorCodeLabel || 'Code', margin + 65, y)
      if (isGrid) {
        doc.text(`Est. ${craftConfig.unitLabel.charAt(0).toUpperCase() + craftConfig.unitLabel.slice(1)}`, margin + 105, y)
      }
      y += 1
      doc.setDrawColor(200)
      doc.line(margin, y, pageWidth - margin, y)
      y += 4

      doc.setFont('helvetica', 'normal')
      
      const colorsToShow = ensureArray(gridData?.colors || symbolKey || [])
      
      colorsToShow.forEach((color, index) => {
        if (y > pageHeight - 20) {
          doc.addPage()
          y = 15
        }
        
        // Defensive: color might be a string or missing properties
        if (!color || typeof color !== 'object') return
        
        const hex = color.hex || '#808080'
        const r = parseInt(hex.slice(1, 3), 16) || 128
        const g = parseInt(hex.slice(3, 5), 16) || 128
        const b = parseInt(hex.slice(5, 7), 16) || 128
        doc.setFillColor(r, g, b)
        doc.rect(margin, y - 3, 6, 4, 'F')
        doc.setDrawColor(150)
        doc.rect(margin, y - 3, 6, 4, 'S')

        if (isGrid) {
          doc.setFontSize(10)
          doc.text(color.symbol || SYMBOLS[index], margin + 10, y)
        }
        
        doc.setFontSize(8)
        const colorName = color.name || color.dmc?.name || `Color ${index + 1}`
        doc.text(colorName, margin + 20, y)
        
        // For non-grid crafts, prefer the direct code (may be glass type, hex, etc.)
        // For grid crafts, prefer .code which is now craft-appropriate (DMC for thread, color name for mosaic, etc.)
        const colorCode = isGrid 
          ? (color.code || color.dmc?.code || `Color ${index + 1}`)
          : (color.code || color.hex?.toUpperCase() || `Color ${index + 1}`)
        doc.text(colorCode, margin + 65, y)
        
        if (isGrid) {
          const countVal = color.count || Math.floor((actualWidth * actualHeight) / Math.max(actualColors, 1))
          doc.text(countVal.toLocaleString(), margin + 110, y)
        }
        
        y += 5
      })
      y += 8

      // Materials Section
      if (y > pageHeight - 60) {
        doc.addPage()
        y = 15
      }
      
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Materials Needed', margin, y)
      y += 6

      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      
      const materialsArr = ensureArray(materials)
      const materialsToShow = materialsArr.length > 0 
        ? materialsArr 
        : craftConfig.materials(actualWidth, actualHeight, actualColors)
      
      materialsToShow.forEach((material) => {
        if (y > pageHeight - 15) {
          doc.addPage()
          y = 15
        }
        const materialStr = typeof material === 'string' ? material : String(material || '')
        if (materialStr) doc.text(`• ${materialStr}`, margin + 3, y)
        y += 5
      })
      y += 5

      // Instructions Section
      if (y > pageHeight - 40) {
        doc.addPage()
        y = 15
      }

      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Instructions', margin, y)
      y += 6

      const instructionText = (typeof instructions === 'string' ? instructions : '') || craftConfig.instructions || ''
      const lineHeight = 4
      const maxTextWidth = pageWidth - margin * 2

      // Parse instructions line by line for basic formatting
      const rawLines = instructionText.split('\n')
      for (const rawLine of rawLines) {
        const trimmed = rawLine.trim()
        if (!trimmed) { y += 3; continue }

        // Check if we need a new page
        if (y > pageHeight - 15) {
          doc.setFontSize(7)
          doc.setTextColor(150)
          doc.text(`Generated by Zenna - Your Ultimate Craft Companion • ${new Date(generatedAt || Date.now()).toLocaleDateString()}`, pageWidth / 2, pageHeight - 8, { align: 'center' })
          doc.text('zennago.com', pageWidth / 2, pageHeight - 4, { align: 'center' })
          doc.addPage()
          y = 15
        }

        // Section header: **BOLD TEXT** or **BOLD:**
        if (/^\*\*[A-Z]/.test(trimmed) && (trimmed.endsWith('**') || trimmed.includes(':**'))) {
          const headerText = trimmed.replace(/\*\*/g, '')
          y += 3
          doc.setFontSize(10)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(60, 20, 120)
          const splitHeader = doc.splitTextToSize(headerText, maxTextWidth)
          splitHeader.forEach(line => {
            if (y > pageHeight - 15) { doc.addPage(); y = 15 }
            doc.text(line, margin, y)
            y += 5
          })
          doc.setTextColor(0)
          y += 1
          continue
        }

        // Numbered step or bullet point
        const isStep = /^\d+\./.test(trimmed)
        const isBullet = /^[-•●]/.test(trimmed)

        // Clean markdown bold from inline text
        const cleanText = trimmed.replace(/\*\*/g, '')

        doc.setFontSize(9)
        doc.setFont('helvetica', isStep ? 'bold' : 'normal')

        const indent = (isStep || isBullet) ? margin + 2 : margin
        const wrapWidth = (isStep || isBullet) ? maxTextWidth - 2 : maxTextWidth
        const splitLines = doc.splitTextToSize(cleanText, wrapWidth)

        splitLines.forEach((line, idx) => {
          if (y > pageHeight - 15) {
            doc.setFontSize(7); doc.setTextColor(150)
            doc.text(`Generated by Zenna - Your Ultimate Craft Companion • ${new Date(generatedAt || Date.now()).toLocaleDateString()}`, pageWidth / 2, pageHeight - 8, { align: 'center' })
            doc.text('zennago.com', pageWidth / 2, pageHeight - 4, { align: 'center' })
            doc.addPage(); y = 15
            doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0)
          }
          doc.text(line, idx === 0 ? indent : indent + 3, y)
          y += lineHeight
        })

        if (isStep) {
          doc.setFont('helvetica', 'normal')
          y += 1
        }
      }
      y += 10

      // Footer on page 1
      doc.setFontSize(7)
      doc.setTextColor(150)
      doc.text(`Generated by Zenna - Your Ultimate Craft Companion • ${new Date(generatedAt || Date.now()).toLocaleDateString()}`, pageWidth / 2, pageHeight - 8, { align: 'center' })
      doc.text('zennago.com', pageWidth / 2, pageHeight - 4, { align: 'center' })

      // ===== PAGE 2+: GRID CHART (grid-based only) or DESIGN IMAGE (design-based) =====
      
      if (isGrid && gridData && gridData.grid) {
        // --- GRID-BASED: draw symbol chart ---
        doc.addPage()
        
        const gridMargin = 8
        const availableWidth = pageWidth - gridMargin * 2
        const availableHeight = pageHeight - 25
        
        const cellSize = Math.min(
          availableWidth / gridData.width,
          availableHeight / gridData.height,
          2.5
        )
        
        const gridWidth = cellSize * gridData.width
        const gridHeight = cellSize * gridData.height
        
        const startX = (pageWidth - gridWidth) / 2
        let startY = 12
        
        const rowsPerPage = Math.floor((pageHeight - 25) / cellSize)
        
        const colorLookup = new Map()
        gridData.colors.forEach(c => {
          colorLookup.set(c.hex, c)
        })
        
        let currentRow = 0
        let pageNum = 2
        
        while (currentRow < gridData.height) {
          if (currentRow > 0) {
            doc.addPage()
          }
          
          doc.setFontSize(10)
          doc.setTextColor(0)
          doc.setFont('helvetica', 'bold')
          doc.text(`${title} - Chart Page ${pageNum - 1}`, pageWidth / 2, 8, { align: 'center' })
          doc.setFontSize(7)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(100)
          const rowEnd = Math.min(currentRow + rowsPerPage, gridData.height)
          doc.text(`Rows ${currentRow + 1} - ${rowEnd}`, pageWidth / 2, 12, { align: 'center' })
          doc.setTextColor(0)
          
          startY = 16
          
          const endRow = Math.min(currentRow + rowsPerPage, gridData.height)
          
          for (let row = currentRow; row < endRow; row++) {
            const py = startY + (row - currentRow) * cellSize
            
            for (let col = 0; col < gridData.width; col++) {
              const px = startX + col * cellSize
              const hex = gridData.grid[row][col]
              const colorInfo = colorLookup.get(hex)
              
              const r = parseInt(hex.slice(1, 3), 16)
              const g = parseInt(hex.slice(3, 5), 16)
              const b = parseInt(hex.slice(5, 7), 16)
              doc.setFillColor(r, g, b)
              doc.rect(px, py, cellSize, cellSize, 'F')
              
              doc.setDrawColor(200)
              doc.setLineWidth(0.1)
              doc.rect(px, py, cellSize, cellSize, 'S')
              
              if (colorInfo?.symbol && cellSize >= 1.5) {
                const brightness = (r * 299 + g * 587 + b * 114) / 1000
                doc.setTextColor(brightness > 128 ? 0 : 255)
                doc.setFontSize(cellSize * 2.2)
                doc.text(colorInfo.symbol, px + cellSize / 2, py + cellSize * 0.75, { align: 'center' })
              }
            }
            
            doc.setTextColor(100)
            doc.setFontSize(5)
            doc.text(`${row + 1}`, startX - 3, py + cellSize * 0.7, { align: 'right' })
          }
          
          for (let col = 0; col < gridData.width; col += 10) {
            const px = startX + col * cellSize + cellSize / 2
            doc.setTextColor(100)
            doc.setFontSize(5)
            doc.text(`${col + 1}`, px, startY - 1, { align: 'center' })
          }
          
          doc.setDrawColor(100)
          doc.setLineWidth(0.3)
          for (let col = 0; col <= gridData.width; col += 10) {
            const px = startX + col * cellSize
            doc.line(px, startY, px, startY + (endRow - currentRow) * cellSize)
          }
          for (let row = currentRow; row <= endRow; row += 10) {
            if (row >= currentRow) {
              const py = startY + (row - currentRow) * cellSize
              doc.line(startX, py, startX + gridWidth, py)
            }
          }
          
          doc.setFontSize(6)
          doc.setTextColor(150)
          doc.text('zennago.com', pageWidth / 2, pageHeight - 3, { align: 'center' })
          
          currentRow = endRow
          pageNum++
        }
      } else if (!isGrid && resolvedCanvasImage) {
        // --- DESIGN-BASED: embed design image ---
        try {
          doc.addPage()
          doc.setFontSize(10)
          doc.setTextColor(0)
          doc.setFont('helvetica', 'bold')
          doc.text(`${title} - Design Reference`, pageWidth / 2, 10, { align: 'center' })
          
          doc.setFontSize(7)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(100)
          doc.text('Use this image as your design template — print at desired size', pageWidth / 2, 15, { align: 'center' })
          doc.setTextColor(0)
          
          // Load and embed the image
          const img = new Image()
          img.crossOrigin = 'anonymous'
          await new Promise((resolve, reject) => {
            img.onload = resolve
            img.onerror = reject
            img.src = resolvedCanvasImage
          })
          
          const imgCanvas = document.createElement('canvas')
          const imgCtx = imgCanvas.getContext('2d')
          imgCanvas.width = img.naturalWidth
          imgCanvas.height = img.naturalHeight
          imgCtx.drawImage(img, 0, 0)
          const imgDataUrl = imgCanvas.toDataURL('image/jpeg', 0.95)
          
          const maxW = pageWidth - margin * 2
          const maxH = pageHeight - 35
          const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight)
          const imgW = img.naturalWidth * ratio
          const imgH = img.naturalHeight * ratio
          const imgX = (pageWidth - imgW) / 2
          
          doc.addImage(imgDataUrl, 'JPEG', imgX, 20, imgW, imgH)
          
          doc.setFontSize(6)
          doc.setTextColor(150)
          doc.text('zennago.com', pageWidth / 2, pageHeight - 3, { align: 'center' })
        } catch (imgError) {
          console.error('Failed to embed design image:', imgError)
        }
      }

      // Save
      const fileName = `${title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${craftType}-pattern.pdf`
      doc.save(fileName)

    } catch (error) {
      console.error('PDF generation failed:', error)
      alert('Failed to generate PDF. Please try again.')
    } finally {
      setIsGenerating(false)
    }
  }

  const actualColors = gridData?.colors?.length || colorCount || 12
  const displayColors = ensureArray(gridData?.colors || symbolKey || [])

  // Determine the right icon for size stat
  const SizeIcon = isGrid ? Grid3X3 : Ruler

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-display font-bold text-slate-900 dark:text-white">Pattern Ready!</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Download your printable PDF</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Pattern Title — dynamic craft label */}
          <div className="text-center py-2">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">{title}</h3>
            <p className="text-sm text-purple-600 dark:text-purple-400 uppercase tracking-wide">
              {craftLabel} Pattern
            </p>
          </div>

          {/* Analysis Status */}
          {isAnalyzing && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 flex items-center gap-3">
              <svg className="w-5 h-5 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm text-blue-700 dark:text-blue-300">
                {isGrid ? 'Analyzing pattern colors...' : 'Extracting color palette...'}
              </span>
            </div>
          )}

          {/* Quick Stats — craft-aware labels */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 flex items-center gap-3">
              <SizeIcon className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {isGrid ? 'Size' : 'Type'}
                </p>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">
                  {isGrid 
                    ? `${gridData?.width || dimensions?.width || 80} x ${gridData?.height || dimensions?.height || 80}`
                    : craftLabel
                  }
                </p>
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 flex items-center gap-3">
              <Palette className="w-5 h-5 text-pink-500" />
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Colors</p>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">{actualColors} {craftConfig.colorLabel}</p>
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-500" />
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Est. Time</p>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">{estimatedTime || '8-16 hours'}</p>
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 flex items-center gap-3">
              <Scissors className="w-5 h-5 text-emerald-500" />
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Difficulty</p>
                <p className="font-semibold text-slate-900 dark:text-white text-sm capitalize">{difficulty || 'Intermediate'}</p>
              </div>
            </div>
          </div>

          {/* Color Preview */}
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Color Palette</p>
            <div className="flex flex-wrap gap-2">
              {displayColors.slice(0, 12).map((color, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-700 rounded-lg px-2 py-1"
                >
                  <div
                    className="w-4 h-4 rounded border border-slate-300 dark:border-slate-500"
                    style={{ backgroundColor: color.hex || '#808080' }}
                  />
                  {isGrid && (
                    <span className="text-xs font-mono text-slate-600 dark:text-slate-300">{color.symbol || SYMBOLS[i]}</span>
                  )}
                  <span className="text-xs text-slate-500 dark:text-slate-400">{isGrid ? (color.code || color.dmc?.code || color.name || `Color ${i + 1}`) : (color.code || color.name || `Color ${i + 1}`)}</span>
                </div>
              ))}
              {displayColors.length > 12 && (
                <span className="text-xs text-slate-400 self-center">+{displayColors.length - 12} more</span>
              )}
            </div>
          </div>

          {/* PDF Includes — craft-specific */}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3">
            <p className="text-sm font-medium text-purple-900 dark:text-purple-300 mb-2">Your PDF includes:</p>
            <ul className="text-sm text-purple-700 dark:text-purple-400 space-y-1">
              {(craftConfig.pdfIncludes || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 p-4 rounded-b-2xl">
          <button
            onClick={generatePDF}
            disabled={isGenerating || isAnalyzing}
            className="w-full py-3 px-4 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isGenerating ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Generating PDF...
              </>
            ) : isAnalyzing ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Analyzing Pattern...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Download {craftLabel} Pattern PDF
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}